import React from 'react';

import { getDateWithoutTimezoneShift } from '@lender/utils';
import { LoanApplicationTabId } from 'app/pages/loanApplication/LoanApplication.constants';
import {
  checkIsAmountOfRentCollectedActive,
  getCurrentEntityPendingChanges,
  getFormattedFullAddress,
} from 'app/pages/loanApplication/LoanApplication.utils';
import { NoAvailableMessage } from 'app/shared/components/common/NoAvailableMessage';
import dayjs from 'dayjs';
import _ from 'lodash';

import { COMMON_REQUEST_DATE_FORMAT, NUMBER_MONTHS_IN_YEAR } from 'constants/date';
import { FieldType } from 'constants/formGenerator';
import {
  currencyOptions,
  LoanEngineType,
  LoanOptionSelectorType,
  LoanPurposeType,
  LoanType,
  LockExtensionsRequestType,
  MiPaymentType,
  MiProductType,
  MiRenewalType,
  NationalHousingActSection,
  PropertyRightsEstateHeld,
  PropertyStatusType,
  PropertyType,
  SourceOfFundsType,
  TangibleNetBenefitReasonType,
  TransactionDetailType,
} from 'constants/loanApplication';
import { LoanChangesHistoryEntityType } from 'constants/loanChanges';
import {
  HistoryPendingChangesActionType,
  HistoryTypeToPendingChangesTypeMap,
  PendingChangesActionType,
  mirroredLoanProgramFields,
} from 'constants/pendingChanges';
import {
  LoanProgramFieldName,
  MinimumInitialDrawType,
  RateType,
  TBD_AFTER_SAVE,
} from 'constants/programs';
import { checkIsTexasState } from 'constants/states';
import {
  getConditionalArray,
  getConditionalObject,
  getConditionalValue,
  normalizeRequestPhoneNumber,
  normalizeViewPhoneNumber,
  replaceEmptyWithFalse,
  replaceEmptyWithNull,
  sortArrayByDate,
  trimBodyStringValues,
} from 'utils/data';
import { getCommonDateBody } from 'utils/date';
import { getFormOptionValue, isBooleanToggleField } from 'utils/form';
import {
  checkIsAdditionalLoanFlow,
  checkIsChattelLoanPurpose,
  checkIsRefinanceCashOutSubflow,
  checkIsRefinanceFlow,
  getPersonFullName,
} from 'utils/loanApplication';
import { getConfigInfo } from 'utils/loanManagement';
import { formatNumberToCurrencyString, roundToDecimals } from 'utils/number';
import { getPatch } from 'utils/patchValue';
import {
  checkBooleanPendingChangeVisibility,
  checkIsPendingChangeHasDifference,
  composeOwnedEntityPendingChangeKey,
  composeOwnedEntityPendingChangeKeyMaps,
  fillPendingChangesWithActionType,
  filterOptionalVisiblePendingChanges,
  formatDatePendingChange,
  mergePendingChanges,
  normalizePendingChanges,
  replaceNestedFieldsActionTypeToModified,
} from 'utils/pendingChanges';
import { startCasePreserveSymbols } from 'utils/string';

import { NEW_RECORD_ID } from '../../PropertyAndLoan.contacts';

import { emptyMessageCellStyles } from './Content.styles';
import { getAdditionalMortgageKey } from './MainContent/AdditionalFinancing/AdditionalMortgageForm/AdditionalMortgageForm.config';
import {
  agentAndSellerModifiedFieldsToExclude,
  agentsAndSellersPendingChangesSectionKey,
  TableDataType,
} from './MainContent/AgentAndSeller/AgentAndSeller.constants';
import { appraisalConfigFields } from './MainContent/Appraisal/Appraisal.config';
import {
  APPRAISAL_ORDER_CONTACTS_KEY,
  orderContactModifiedFieldsToExclude,
} from './MainContent/Appraisal/components/AppraisalOrderContactsCard/AppraisalOrderContactsCard.constants';
import {
  checkIsOrderContactsChanged,
  excludePendingDeletedOrderContacts,
  getAppraisalOrderContactsRequestData,
} from './MainContent/Appraisal/components/AppraisalOrderContactsCard/AppraisalOrderContactsCard.utils';
import { chattelHomeDetailsScalarFieldKeys } from './MainContent/ChattelHomeDetails/ChattelHomeDetails.constants';
import {
  checkIsChattelCommunityPark,
  checkIsChattelLandContract,
  checkIsChattelLandownerPlacement,
  checkIsChattelLeasedPrivateProperty,
  getEffectiveChattelSitePlacement,
} from './MainContent/ChattelProperty/ChattelProperty.config';
import { creditModifiedFieldsToExclude } from './MainContent/Credits/Credits.constants';
import {
  getAdditionalLoanInformationConfig,
  getDocumentVerificationTypesConfig,
  getLoanDetailsConfig,
  getNonQmLoanInfoConfig,
  loanInfoConfigFields,
  loanInfoSectionFieldsKeys,
} from './MainContent/LoanInfo/LoanInfo.config';
import {
  LOAN_ADDITIONAL_INFO_PATCH_KEYS,
  LOAN_DETAILS_PATCH_KEYS,
  LOAN_DETAILS_RECALCULATED_KEY_SOURCES,
  LOAN_DOCUMENT_VERIFICATION_TYPES_PATCH_KEYS,
  LOAN_NON_QM_PATCH_KEYS,
  RATE_DECIMAL_SCALE,
} from './MainContent/LoanInfo/LoanInfo.constants';
import {
  checkIsPrepaymentPenaltyChoiceVisible,
  rebaseDerivedLoanInfoPendingChanges,
} from './MainContent/LoanInfo/LoanInfo.utils';
import { loanProgramFieldNames } from './MainContent/LoanInfo/LoanProgramSection.config';
import { LOAN_PROGRAM_CHANGE_SOURCES } from './MainContent/LoanInfo/LoanProgramSection.constants';
import {
  MANUAL_BUY_SIDE_FIELD_NAMES,
  MANUAL_SELL_SIDE_FIELD_NAMES,
  PROFIT_AND_LOSS_CHANGE_SOURCES,
  PROFIT_LOSS_RESULT_FIELD_NAMES,
  ProfitAndLossFieldName,
} from './MainContent/LoanPL/LoanPL.constants';
import {
  lockInfoSectionFieldsKeys,
  TotalAdjustmentsField,
} from './MainContent/LockInfo/LockInfo.config';
import {
  adjustmentsModifiedFieldsToExclude,
  CustomAdjustmentType,
} from './MainContent/LockInfo/LockInfo.constants';
import {
  getMortgageInsuranceConfig,
  mortgageInsuranceSectionFieldsKeys,
} from './MainContent/MortgageInsurance/MortgageInsurance.config';
import { programAndPricingPendingChangesFields } from './MainContent/ProgramsAndPricing/ProgramAndPricing.constants';
import { projectConfigFields } from './MainContent/Project/Project.config';
import {
  propertyConfigFields,
  propertySectionFieldsKeys,
} from './MainContent/Property/Property.config';
import {
  checkIsManufacturedHome,
  checkIsManufacturedHomePendingChange,
  checkIsMixedUseVisible,
  checkIsMixedUseVisiblePendingChange,
} from './MainContent/Property/Property.utils';
import { OtherExpensesInfo } from './MainContent/PropertyExpenses/OtherExpenses/OtherExpenses.constants';
import { extractOtherExpensesPendingChangesFromJson } from './MainContent/PropertyExpenses/OtherExpenses/OtherExpenses.utils';
import {
  existingPropertyExpensesFields,
  proposedPropertyExpensesFields,
} from './MainContent/PropertyExpenses/PropertyExpenses.config';
import {
  otherExpensesModifiedFieldsToExclude,
  PROPERTY_EXPENSES_DERIVED_KEYS,
  PROPERTY_EXPENSES_PATCH_KEYS,
  PROPERTY_EXPENSES_RENT_KEYS,
  propertyExpensesModifiedFieldsToExclude,
  propertyExpensesPendingChangesAllowedEntityTypeNames,
} from './MainContent/PropertyExpenses/PropertyExpenses.constants';
import {
  checkIsAmountOfRentCollectedActivePendingChange,
  mapProposedPropertyExpensesFields,
  prepareNestedProposedPropertyExpensesToSend,
} from './MainContent/PropertyExpenses/PropertyExpenses.utils';
import { PropertyTaxesInfo } from './MainContent/PropertyExpenses/PropertyTaxes/PropertyTaxes.constants';
import { SupplementalInsuranceInfo } from './MainContent/PropertyExpenses/SupplementalInsurance/SupplementalInsurance.constants';
import { SellSideLockFieldName } from './MainContent/SellSideLock/SellSideLock.constants';
import {
  transactionDetailsConfigFields,
  transactionDetailsFieldsKeys,
} from './MainContent/TransactionDetails/TransactionDetails.config';
import {
  closingTypeVisibility,
  constructionOrImprovementCostsVisibility,
} from './MainContent/TransactionDetails/TransactionDetails.constants';

const deferredThreshold = 60;

export const optionalVisibleFieldsTriggers = [
  {
    fieldName: 'isDeedRestricted',
    validation: ({ pendingChange }) => !pendingChange?.newValue,
    dependents: ['deedRestrictionType', 'deedRestrictionMonths'],
  },
  {
    fieldName: 'propertyType',
    validation: ({ pendingChange, applicationData }) =>
      ![undefined, PropertyType.MULTI_UNIT].includes(
        pendingChange?.newValue ?? applicationData.propertyType,
      ),
    dependents: ['numberOfUnits'],
  },
  {
    fieldName: transactionDetailsConfigFields.transactionDetail.nameInModel,
    validation: ({ pendingChange }) =>
      !constructionOrImprovementCostsVisibility.includes(pendingChange?.newValue),
    dependents: [transactionDetailsConfigFields.constructionOrImprovementCosts.nameInModel],
  },
  {
    fieldName: transactionDetailsConfigFields.transactionDetail.nameInModel,
    validation: ({ pendingChange }) => !closingTypeVisibility.includes(pendingChange?.newValue),
    dependents: [transactionDetailsConfigFields.transactionDetailClosingType.nameInModel],
  },
  {
    fieldName: 'loanType',
    validation: ({ pendingChange, loanType }) =>
      ![undefined, LoanType.VA].includes(pendingChange?.newValue ?? loanType),
    dependents: ['propertySquareFootage'],
  },
  {
    fieldName: 'propertyRightsEstateHeld',
    validation: ({ pendingChange }) =>
      ![undefined, PropertyRightsEstateHeld.LEASE_HOLD].includes(pendingChange?.newValue),
    dependents: ['leaseholdExpiration'],
  },
  {
    fieldName: 'state',
    validation: ({ pendingChange }) =>
      !(pendingChange?.newValue === undefined || checkIsTexasState(pendingChange?.newValue)),
    dependents: [propertyConfigFields.hasOweltyOfPartition.nameInModel],
  },
  {
    fieldName: 'propertyType',
    validation: ({ pendingChange }) =>
      ![undefined, PropertyType.SINGLE_FAMILY].includes(pendingChange?.newValue),
    dependents: [propertyConfigFields.mixedUseProperty.nameInModel],
  },
  {
    fieldName: 'propertyType',
    validation: ({ pendingChange, applicationData }) =>
      ![undefined, PropertyType.CONDO].includes(
        pendingChange?.newValue ?? applicationData.propertyType,
      ),
    dependents: [propertyConfigFields.isCondotel.nameInModel],
  },
  {
    fieldName: 'isAmountOfRentCollectedActive',
    validation: checkIsAmountOfRentCollectedActivePendingChange,
    dependents: [
      proposedPropertyExpensesFields.rentCalculatedBasedOn.nameInModel,
      proposedPropertyExpensesFields.monthlyRentalIncome.nameInModel,
      proposedPropertyExpensesFields.mortgagesPayment.nameInModel,
      proposedPropertyExpensesFields.expenses.nameInModel,
      proposedPropertyExpensesFields.netMonthlyRentalIncome.nameInModel,
    ],
  },
  {
    fieldName: 'isManufacturedHome',
    validation: checkIsManufacturedHomePendingChange,
    dependents: [propertyConfigFields.manufacturedHomeType.nameInModel],
  },
  {
    fieldName: 'isMixedUseVisible',
    validation: checkIsMixedUseVisiblePendingChange,
    dependents: [propertyConfigFields.mixedUseProperty.nameInModel],
  },
  {
    fieldName: 'loanPurpose',
    validation: ({ isPurchase }) => !isPurchase,
    dependents: [
      'totalSalesConcessionAmount',
      'propertyValue',
      propertyConfigFields.isRelocationLoan.nameInModel,
    ],
  },
  {
    fieldName: propertyConfigFields.isNewConstruction.nameInModel,
    validation: ({ pendingChange }) => !pendingChange?.newValue,
    dependents: [propertyConfigFields.completionDate.nameInModel],
  },
  {
    fieldName: propertyConfigFields.hasAffordableLegalRestrictions.nameInModel,
    validation: ({ pendingChange }) => !pendingChange?.newValue,
    dependents: [propertyConfigFields.isConveyable.nameInModel],
  },
];

const fieldsPendingChangeNormalizers = [
  {
    fieldName: 'appraisal.uniformCollateralDataPortal.units',
    normalize: ({ pendingChanges, pendingChange, applicationData }) =>
      pendingChange?.slice(
        0,
        (pendingChanges.propertyType
          ? pendingChanges.numberOfUnits?.newValue
          : applicationData.numberOfUnits) ?? 1,
      ),
  },
];

const normalizePropertyAndLoanOptionalVisibleFields = ({
  pendingChanges,
  applicationData,
  ...props
}) => {
  const ref = { current: pendingChanges };

  optionalVisibleFieldsTriggers.forEach(({ fieldName, validation, dependents }) => {
    const isValid = validation({
      pendingChange: _.get(ref.current, fieldName),
      applicationData,
      pendingChanges,
      ...props,
    });

    if (!isValid) {
      return;
    }

    ref.current = _.omit(ref.current, dependents);
  });

  fieldsPendingChangeNormalizers.forEach(({ fieldName, normalize }) => {
    const fieldPendingChange = _.get(ref.current, fieldName);

    if (!fieldPendingChange) {
      return;
    }

    const normalizedPendingChange = normalize({
      pendingChanges: ref.current,
      pendingChange: fieldPendingChange,
      applicationData,
      ...props,
    });

    _.set(ref.current, fieldName, normalizedPendingChange);
  });

  return ref.current;
};

const normalizeTableEntitiesFields = ({
  entities,
  initialEntitiesData,
  pendingChangesData,
  omittedFields = [],
}) => {
  const initialDataMap = _.keyBy(initialEntitiesData, 'id');

  return (entities || initialEntitiesData).reduce((acc, prevEntityData) => {
    const initialEntityData = initialDataMap[prevEntityData.id];

    const historyPendingChanges = pendingChangesData?.[prevEntityData.id] ?? {};

    const entityCurrentPendingChanges = getCurrentEntityPendingChanges({
      initialValues: initialEntityData ?? prevEntityData,
      currentValues: prevEntityData,
      omittedFields,
      actionType: Object.values(historyPendingChanges).some(
        change => change?.actionType === HistoryPendingChangesActionType.ADDED,
      )
        ? HistoryPendingChangesActionType.ADDED
        : null,
    });

    const mergedPendingChanges = pendingChangesData?.[prevEntityData.id]
      ? mergePendingChanges(historyPendingChanges, entityCurrentPendingChanges)
      : entityCurrentPendingChanges;

    acc[prevEntityData.id] = mergedPendingChanges;

    return acc;
  }, {});
};

const getOrderContactsPendingChanges = ({
  pendingChanges,
  entitySideData,
  initialEntitySideData,
}) => ({
  ...pendingChanges[APPRAISAL_ORDER_CONTACTS_KEY],
  ...getConditionalObject(_.has(entitySideData, APPRAISAL_ORDER_CONTACTS_KEY), () =>
    normalizeTableEntitiesFields({
      entities: entitySideData[APPRAISAL_ORDER_CONTACTS_KEY],
      initialEntitiesData: initialEntitySideData?.[APPRAISAL_ORDER_CONTACTS_KEY] ?? [],
      pendingChangesData: pendingChanges[APPRAISAL_ORDER_CONTACTS_KEY],
      omittedFields: orderContactModifiedFieldsToExclude,
    }),
  ),
});

const normalizePropertyAndLoanTablePendingChangesFields = ({
  pendingChanges,
  applicationData,
  entitySideData,
  initialEntitySideData,
}) => {
  const agentsAndSellersPendingChangesData = pendingChanges.agentsAndSellersPendingChanges;

  const mergedSellingAgentsPendingChanges = normalizeTableEntitiesFields({
    entities: entitySideData.sellingAgents,
    initialEntitiesData: applicationData.sellingAgents || [],
    pendingChangesData: agentsAndSellersPendingChangesData,
    omittedFields: agentAndSellerModifiedFieldsToExclude,
  });

  const mergedListingAgentsPendingChanges = normalizeTableEntitiesFields({
    entities: entitySideData.listingAgents,
    initialEntitiesData: applicationData.listingAgents || [],
    pendingChangesData: agentsAndSellersPendingChangesData,
    omittedFields: agentAndSellerModifiedFieldsToExclude,
  });

  const mergedSellersPendingChanges = normalizeTableEntitiesFields({
    entities: entitySideData.sellers,
    initialEntitiesData: applicationData.sellers || [],
    pendingChangesData: agentsAndSellersPendingChangesData,
    omittedFields: agentAndSellerModifiedFieldsToExclude,
  });

  const mergedPropertyTaxesPendingChanges = normalizeTableEntitiesFields({
    entities: entitySideData.propertyTaxes,
    initialEntitiesData: applicationData.proposedPropertyExpenses?.propertyTaxes || [],
    pendingChangesData: pendingChanges.propertyTaxesPendingChanges,
    omittedFields: propertyExpensesModifiedFieldsToExclude,
  });

  const mergedSupplementalInsurancesPendingChanges = normalizeTableEntitiesFields({
    entities: entitySideData.supplementalInsurances,
    initialEntitiesData: applicationData.proposedPropertyExpenses?.supplementalInsurances || [],
    pendingChangesData: pendingChanges.supplementalInsurancesPendingChanges,
    omittedFields: propertyExpensesModifiedFieldsToExclude,
  });

  const mergedOtherExpensesPendingChanges = normalizeTableEntitiesFields({
    entities: entitySideData.otherExpenses,
    initialEntitiesData: applicationData.proposedPropertyExpenses?.otherExpenses || [],
    pendingChangesData: pendingChanges.otherExpensesPendingChanges,
    omittedFields: otherExpensesModifiedFieldsToExclude,
  });

  return {
    ...pendingChanges,
    agentsAndSellersPendingChanges: {
      ...mergedSellingAgentsPendingChanges,
      ...mergedListingAgentsPendingChanges,
      ...mergedSellersPendingChanges,
    },
    propertyTaxesPendingChanges: mergedPropertyTaxesPendingChanges,
    supplementalInsurancesPendingChanges: mergedSupplementalInsurancesPendingChanges,
    otherExpensesPendingChanges: mergedOtherExpensesPendingChanges,
    [APPRAISAL_ORDER_CONTACTS_KEY]: getOrderContactsPendingChanges({
      pendingChanges,
      entitySideData,
      initialEntitySideData,
    }),
  };
};

const unitFields = ['roomsNumber', 'bedroomsNumber', 'bathsNumber', 'rent'];

const existingPropertyExpensesPendingChangesFields = _.mapKeys(
  existingPropertyExpensesFields,
  ({ nameInModel }) =>
    composeOwnedEntityPendingChangeKey({
      parentProperty: LoanChangesHistoryEntityType.EXISTING_PROPERTY_EXPENSES,
      propertyName: nameInModel.split('.').pop(),
    }),
);

const normalizePropertyAndLoanInitialPendingChanges = (pendingChanges, applicationData) => {
  return Object.entries(pendingChanges).reduce((acc, [key, value]) => {
    switch (true) {
      case transactionDetailsFieldsKeys.includes(key):
      case propertySectionFieldsKeys.includes(key):
      case agentsAndSellersPendingChangesSectionKey.includes(key):
        _.set(acc, key, value);
        break;

      case key in appraisalConfigFields:
        {
          const { nameInModel, type } = appraisalConfigFields[key];
          const formattedValue = type === FieldType.DATE ? formatDatePendingChange(value) : value;

          _.set(acc, nameInModel, formattedValue);
        }

        break;

      case key === APPRAISAL_ORDER_CONTACTS_KEY:
        _.set(acc, key, value);
        break;

      case key === 'units':
        {
          const formattedValue = value
            .filter(
              ({ propertyUnitId }) =>
                propertyUnitId?.actionType !== HistoryPendingChangesActionType.DELETED,
            )
            .slice(0, applicationData.numberOfUnits ?? 1)
            .map(pendingChange =>
              replaceNestedFieldsActionTypeToModified(_.pick(pendingChange, unitFields)),
            );

          _.set(acc, 'appraisal.uniformCollateralDataPortal.units', formattedValue);
        }

        break;

      case key in projectConfigFields:
        _.set(acc, projectConfigFields[key].nameInModel, value);
        break;

      case key in proposedPropertyExpensesFields &&
        propertyExpensesPendingChangesAllowedEntityTypeNames.includes(value.entityTypeName):
        _.set(acc, proposedPropertyExpensesFields[key].nameInModel, value);
        break;

      case key === PropertyTaxesInfo.PENDING_CHANGES_KEY:
        _.set(acc, PropertyTaxesInfo.PENDING_CHANGES_KEY, value);
        break;

      case key === SupplementalInsuranceInfo.PENDING_CHANGES_KEY:
        _.set(acc, SupplementalInsuranceInfo.PENDING_CHANGES_KEY, value);
        break;

      case key === OtherExpensesInfo.PENDING_CHANGES_KEY:
        {
          const otherExpenses = extractOtherExpensesPendingChangesFromJson({
            pendingChanges: value,
            applicationData: applicationData?.proposedPropertyExpenses?.otherExpenses,
          });

          _.set(acc, OtherExpensesInfo.PENDING_CHANGES_NAME, otherExpenses);
        }

        break;

      case Object.hasOwn(existingPropertyExpensesPendingChangesFields, key):
        {
          const formattedValue =
            value.actionType === HistoryPendingChangesActionType.ADDED
              ? { ...value, actionType: HistoryPendingChangesActionType.MODIFIED }
              : value;

          _.set(acc, existingPropertyExpensesPendingChangesFields[key].nameInModel, formattedValue);
        }

        break;

      default:
        break;
    }

    return acc;
  }, {});
};

const omittedAdditionalMortgageFields = ['modifiedDate'];

const serverKeyToFormFieldMap = {
  buyDownId: 'buyDown',
};

const pendingChangesToLpFieldMap = {
  loanTerm: LoanProgramFieldName.LOAN_TERM,
  amortTerm: LoanProgramFieldName.AMORT_TERM,
  loanType: LoanProgramFieldName.PROGRAM_LOAN_TYPE,
  amortizationType: LoanProgramFieldName.AMORTIZATION_TYPE,
};

const { fieldNameByPendingChangeKey: loanProgramPendingChangesKeyMap } =
  composeOwnedEntityPendingChangeKeyMaps({
    changeSources: LOAN_PROGRAM_CHANGE_SOURCES,
    excludedFieldNames: mirroredLoanProgramFields,
  });

const {
  fieldNameByPendingChangeKey: profitAndLossPendingChangesKeyMap,
  pendingChangeKeyByFieldName: profitAndLossPendingChangeKeyByFieldName,
} = composeOwnedEntityPendingChangeKeyMaps({ changeSources: PROFIT_AND_LOSS_CHANGE_SOURCES });

const markAsSeparateChange = pendingChange =>
  _.has(pendingChange, 'newValue') ? { ...pendingChange, isSeparateChange: true } : pendingChange;

// The Loan Program field shows the loan's own value, so a saved loan-side change is copied onto it
// for highlighting only; the loan-side entry is the one that counts.
const markAsMirroredChange = pendingChange =>
  _.has(pendingChange, 'newValue') ? { ...pendingChange, isMirroredChange: true } : pendingChange;

const normalizeLoanAndLockInitialPendingChanges = ({
  applicationData,
  pendingChanges,
  isPurchase,
  isAdditionalLoan,
  isHelocPurposePurchase,
  shouldShowLoanRolesSection,
  shouldShowLoanTagsSection,
  shouldShowProfitAndLossSection,
  shouldShowOBCustomFieldsSection,
}) =>
  Object.entries(pendingChanges).reduce(
    (acc, [key, value]) => {
      const fieldKey = serverKeyToFormFieldMap[key] ?? key;

      switch (true) {
        case key === 'totalLoanAmount' &&
          Number(value.newValue) !==
            Number(pendingChanges.loanAmount?.newValue ?? applicationData.baseLoanAmount):
        case ['vaTotalEntitlement', 'cashOutAmount'].includes(key) &&
          Number(value.newValue) !== Number(value.initialValue):
        case loanInfoSectionFieldsKeys.includes(fieldKey) && fieldKey !== 'totalLoanAmount':
        case lockInfoSectionFieldsKeys.includes(fieldKey):
        case mortgageInsuranceSectionFieldsKeys.includes(fieldKey):
        case programAndPricingPendingChangesFields.includes(fieldKey):
        case fieldKey === 'credits':
        case fieldKey === 'loanTags' && shouldShowLoanTagsSection:
        case fieldKey === 'loanRoles' && shouldShowLoanRolesSection:
        case fieldKey === 'obCustomFields' && shouldShowOBCustomFieldsSection:
          _.set(acc, fieldKey, markAsSeparateChange(value));

          if (pendingChangesToLpFieldMap[fieldKey]) {
            _.set(acc, pendingChangesToLpFieldMap[fieldKey], markAsMirroredChange(value));
          }

          break;

        case !!loanProgramPendingChangesKeyMap[key]: {
          const isEmptyChange =
            (_.isNil(value.newValue) ||
              value.newValue === '' ||
              value.newValue === 0 ||
              value.newValue === '0') &&
            (_.isNil(value.initialValue) ||
              value.initialValue === '' ||
              value.initialValue === 0 ||
              value.initialValue === '0');

          if (!isEmptyChange) {
            _.set(acc, loanProgramPendingChangesKeyMap[key], markAsSeparateChange(value));
          }

          break;
        }

        case !!profitAndLossPendingChangesKeyMap[key] && shouldShowProfitAndLossSection:
          {
            const fieldName = profitAndLossPendingChangesKeyMap[key];
            const isAnyManualBuyFieldChanged = MANUAL_BUY_SIDE_FIELD_NAMES.some(
              manualFieldName =>
                pendingChanges[profitAndLossPendingChangeKeyByFieldName[manualFieldName]],
            );
            const isAnyManualSellFieldChanged = MANUAL_SELL_SIDE_FIELD_NAMES.some(
              manualFieldName =>
                pendingChanges[profitAndLossPendingChangeKeyByFieldName[manualFieldName]],
            );
            const isAnyManualFieldChanged =
              isAnyManualBuyFieldChanged || isAnyManualSellFieldChanged;

            if (
              fieldName === ProfitAndLossFieldName.NET_BUY_PRICE ||
              (fieldName === ProfitAndLossFieldName.TOTAL_BUY_PRICE &&
                !isAnyManualBuyFieldChanged) ||
              (fieldName === ProfitAndLossFieldName.TOTAL_SELL_PRICE &&
                !isAnyManualSellFieldChanged) ||
              (PROFIT_LOSS_RESULT_FIELD_NAMES.includes(fieldName) && !isAnyManualFieldChanged)
            ) {
              break;
            }

            _.set(acc, `profitAndLoss.${fieldName}`, value);
          }

          break;

        case Object.values(SellSideLockFieldName).includes(key) && shouldShowProfitAndLossSection:
          _.set(acc, `sellSideLock.${key}`, value);
          break;

        case key === TotalAdjustmentsField.KEY:
          _.set(acc, TotalAdjustmentsField.PATH, value);
          break;

        case key === 'additionalMortgages':
          {
            if (!isPurchase && !(isAdditionalLoan && isHelocPurposePurchase)) {
              return acc;
            }

            const formattedPendingChanges = value.reduce((acc, pendingChangesValues, index) => {
              acc[getAdditionalMortgageKey(index)] = {
                ..._.omit(pendingChangesValues, omittedAdditionalMortgageFields),
                isSeparateChange: true,
              };

              return acc;
            }, {});

            _.set(acc, key, formattedPendingChanges);
          }

          break;

        case key === 'loanAmount':
          _.set(acc, 'baseLoanAmount', value);
          break;

        case key === 'leadSourceId':
          _.set(acc, 'leadSource', value);
          break;

        case key === 'investorFeatureCodeId': {
          if (!value?.length) {
            break;
          }

          const investorCodes = value.reduce((acc, value, index) => {
            if (index === 0) {
              return {
                initialValue: value.initialValue ? [value.initialValue] : [],
                actionType: HistoryPendingChangesActionType.MODIFIED,
                newValue: value.newValue ? [value.newValue] : [],
              };
            }

            if (value.newValue) {
              acc.newValue.push(value.newValue);
            }

            if (value.initialValue) {
              acc.initialValue.push(value.initialValue);
            }

            return acc;
          }, {});

          _.set(acc, 'investorCodes', investorCodes);
          break;
        }

        case key === 'programIdentifierId': {
          if (!value?.length) {
            break;
          }

          const loanProgramIdentifiers = value.reduce((acc, value, index) => {
            if (index === 0) {
              return {
                initialValue: value.initialValue ? [value.initialValue] : [],
                actionType: HistoryPendingChangesActionType.MODIFIED,
                newValue: value.newValue ? [value.newValue] : [],
              };
            }

            if (value.newValue) {
              acc.newValue.push(value.newValue);
            }

            if (value.initialValue) {
              acc.initialValue.push(value.initialValue);
            }

            return acc;
          }, {});

          _.set(acc, 'loanProgramIdentifiers', loanProgramIdentifiers);
          break;
        }

        case key === 'nationalHousingActSections':
          _.set(acc, 'nationalHousingActSection', value);
          break;

        case key === 'fha203KConsultantId':
          _.set(acc, 'fha203kConsultantId', value);
          break;

        case key === 'governmentRefinancePurpose':
          _.set(acc, 'governmentRefinancePurpose', value);
          break;

        case key === 'taxesInsuranceEscrowed':
          _.set(acc, 'escrowSettings', {
            ...(acc.escrowSettings ?? {}),
            isEscrowed: value,
          });
          break;

        case key === 'taxesEscrowed':
          _.set(acc, 'escrowSettings', {
            ...(acc.escrowSettings ?? {}),
            taxesEscrowSettings: { isEscrowed: value },
          });
          break;

        case key === 'insuranceImpoundsEscrowed':
          _.set(acc, 'escrowSettings', {
            ...(acc.escrowSettings ?? {}),
            insuranceEscrowSettings: value,
          });
          break;

        case key === 'documentVerificationAsset':
          _.set(acc, 'documentVerificationAssetType', value);
          break;

        case key === 'documentVerificationEmployment':
          _.set(acc, 'documentVerificationEmploymentType', value);
          break;

        case key === 'documentVerificationIncome':
          _.set(acc, 'documentVerificationIncomeType', value);
          break;

        default:
          break;
      }

      return acc;
    },
    {
      loanTags: {},
      loanRoles: {},
      credits: {},
      profitAndLoss: {},
      additionalMortgages: {},
    },
  );

export const normalizePropertyAndLoanPendingChangesAction = ({
  initialPendingChanges,
  formPendingChanges,
  applicationData,
  entitySideData,
  initialEntitySideData,
  loanType,
  isPurchase,
}) => {
  const normalizedInitialPendingChanges = normalizePropertyAndLoanInitialPendingChanges(
    initialPendingChanges,
    applicationData,
  );

  const mergedPendingChanges = mergePendingChanges(
    normalizedInitialPendingChanges,
    formPendingChanges,
  );

  const normalizedOptionalVisibleFieldsPendingChanges =
    normalizePropertyAndLoanOptionalVisibleFields({
      pendingChanges: mergedPendingChanges,
      applicationData,
      loanType,
      isPurchase,
    });

  const normalizedPendingChanges = normalizePropertyAndLoanTablePendingChangesFields({
    pendingChanges: normalizedOptionalVisibleFieldsPendingChanges,
    applicationData,
    entitySideData,
    initialEntitySideData,
  });

  return normalizedPendingChanges;
};

const loanAndLockOptionalVisibleFieldsTriggers = [
  {
    fieldName: 'sourceOfFunds',
    validation: ({ pendingChange }) =>
      ![undefined, SourceOfFundsType.OTHER].includes(pendingChange?.newValue),
    dependents: ['otherFundDescription'],
  },
  {
    fieldName: 'heloc',
    validation: ({ pendingChange }) => ![undefined, false].includes(pendingChange?.newValue),
    dependents: ['isAffordableLoan'],
  },
  {
    fieldName: 'heloc',
    validation: ({ pendingChange }) => ![undefined, true].includes(pendingChange?.newValue),
    dependents: ['highLimit'],
  },
  {
    fieldName: 'monthsDeferred',
    validation: ({ pendingChange }) => Number(pendingChange?.newValue) <= deferredThreshold,
    dependents: ['deferredType'],
  },
  {
    fieldName: 'loanPurpose',
    validation: ({ pendingChange, applicationData }) =>
      ![undefined, LoanPurposeType.REFINANCE].includes(
        pendingChange?.newValue ?? applicationData.loanPurpose,
      ),
    dependents: ['refinancePurpose', 'governmentRefinancePurpose'],
  },
  {
    fieldName: 'loanPurpose',
    validation: ({ pendingChange, applicationData }) =>
      ![undefined, LoanPurposeType.ADDITIONAL_LOAN].includes(
        pendingChange?.newValue ?? applicationData.loanPurpose,
      ),
    dependents: ['additionalLoanSecondMortgageType', 'additionalLoanDrawAmount', 'helocPurpose'],
  },
  {
    fieldName: 'loanPurpose',
    validation: ({ pendingChange, applicationData }) =>
      ![undefined, LoanPurposeType.REFINANCE].includes(
        pendingChange?.newValue ?? applicationData.loanPurpose,
      ),
    dependents: ['cashOutAmount'],
  },
  {
    fieldName: 'refinancePurpose',
    validation: ({ pendingChange }) =>
      pendingChange?.newValue !== undefined &&
      !checkIsRefinanceCashOutSubflow({
        refinancePurpose: pendingChange?.newValue,
      }),
    dependents: ['cashOutAmount'],
  },
  {
    fieldName: 'loanType',
    validation: ({ pendingChange, applicationData }) =>
      ![undefined, LoanType.HELOC].includes(
        pendingChange?.newValue ?? applicationData.mortgageType,
      ),
    dependents: ['additionalLoanDrawAmount'],
  },
  {
    fieldName: 'loanType',
    validation: ({ pendingChange, applicationData }) =>
      ![undefined, LoanType.FHA].includes(pendingChange?.newValue ?? applicationData.mortgageType),
    dependents: [
      'nationalHousingActSection',
      'fha203kConsultantId',
      'fhaBorrowerCertification',
      'housingFinanceAgencyId',
      'housingFinanceAgencyOtherDescription',
      'hudFieldOfficeCode',
      'hudFieldOfficeName',
      'hudHocCode',
      'hudHocName',
      'fhaInspectorIdentifier',
    ],
  },
  {
    fieldName: 'nationalHousingActSection',
    validation: ({ pendingChange }) =>
      pendingChange?.newValue !== undefined &&
      ![
        NationalHousingActSection.SECTION_203K_FHA_REHABILITATION_LOAN_INSURANCE,
        NationalHousingActSection.SECTION_203K251_PURCHASE_REHAB_ARM,
      ].includes(pendingChange?.newValue),
    dependents: ['fha203kConsultantId'],
  },
  {
    fieldName: 'loanType',
    validation: ({ pendingChange, applicationData }) =>
      ![undefined, LoanType.VA].includes(pendingChange?.newValue ?? applicationData.mortgageType),

    dependents: ['minimumResidualIncome', 'vaTotalEntitlement'],
  },
  {
    fieldName: 'loanType',
    validation: ({ pendingChange, applicationData }) =>
      ![undefined, LoanType.VA, LoanType.FHA].includes(
        pendingChange?.newValue ?? applicationData.mortgageType,
      ),

    dependents: ['agencyCaseNumber', 'agencyCaseNumberIssueDate'],
  },
  {
    fieldName: 'loanType',
    validation: ({ pendingChange, applicationData }) =>
      ![undefined, LoanType.FHA, LoanType.VA, LoanType.USDA].includes(
        pendingChange?.newValue ?? applicationData.mortgageType,
      ),
    dependents: ['governmentRefinancePurpose'],
  },
  {
    fieldName: 'loanPurpose',
    validation: ({ pendingChange, applicationData }) =>
      ![undefined, LoanPurposeType.REFINANCE].includes(
        pendingChange?.newValue ?? applicationData.loanPurpose,
      ),
    dependents: [
      'tangibleNetBenefitReason',
      'tangibleNetBenefitCurrentInterestRate',
      'tangibleNetBenefitCurrentAmortizationPeriodMonths',
      'tangibleNetBenefitDescription',
    ],
  },
  {
    fieldName: 'tangibleNetBenefitReason',
    validation: ({ pendingChange }) =>
      pendingChange?.newValue !== undefined &&
      pendingChange?.newValue !== TangibleNetBenefitReasonType.LOWER_INTEREST_RATE,
    dependents: ['tangibleNetBenefitCurrentInterestRate'],
  },
  {
    fieldName: 'tangibleNetBenefitReason',
    validation: ({ pendingChange }) =>
      pendingChange?.newValue !== undefined &&
      pendingChange?.newValue !==
        TangibleNetBenefitReasonType.BENEFICIAL_CHANGE_IN_AMORTIZATION_PERIOD,
    dependents: ['tangibleNetBenefitCurrentAmortizationPeriodMonths'],
  },
  {
    fieldName: 'tangibleNetBenefitReason',
    validation: ({ pendingChange }) =>
      pendingChange?.newValue !== undefined &&
      pendingChange?.newValue !== TangibleNetBenefitReasonType.OTHER,
    dependents: ['tangibleNetBenefitDescription'],
  },
  {
    fieldName: 'housingFinanceAgencyId',
    validation: ({ pendingChange }) =>
      pendingChange?.newValue !== undefined && !pendingChange.isOther,
    dependents: ['housingFinanceAgencyOtherDescription'],
  },
  {
    fieldName: 'isLoanNoPpeEngineType',
    validation: ({ isLoanNoPpeEngineType }) => isLoanNoPpeEngineType,
    dependents: ['leadSource'],
  },
  {
    fieldName: LoanProgramFieldName.CHAR_ARM,
    validation: ({ pendingChange, applicationData }) =>
      !(pendingChange?.newValue ?? applicationData[LoanProgramFieldName.CHAR_ARM]),
    dependents: [
      LoanProgramFieldName.ARM_INITIAL_CAP,
      LoanProgramFieldName.ARM_ADJUSTMENT_CAP,
      LoanProgramFieldName.ARM_SUBSEQUENT_CAP,
      LoanProgramFieldName.ARM_RATE_ADJ_PERIOD,
      LoanProgramFieldName.ARM_SUB_RATE_ADJ_PERIOD,
      LoanProgramFieldName.ARM_FLOOR_RATE,
      LoanProgramFieldName.ARM_CEILING_RATE,
      LoanProgramFieldName.ARM_ADJ_CHANGE_INDEX_RATE,
    ],
  },
  {
    fieldName: LoanProgramFieldName.CHAR_ARM,
    validation: ({ pendingChange, pendingChanges, applicationData }) =>
      !(pendingChange?.newValue ?? applicationData[LoanProgramFieldName.CHAR_ARM]) &&
      (pendingChanges[LoanProgramFieldName.PROGRAM_LOAN_TYPE]?.newValue ??
        applicationData[LoanProgramFieldName.PROGRAM_LOAN_TYPE]) !== LoanType.HELOC,
    dependents: [
      LoanProgramFieldName.INDEX_TYPE_ID,
      LoanProgramFieldName.INDEX_MARGIN,
      LoanProgramFieldName.INDEX_RATE,
      LoanProgramFieldName.INDEX_PERIOD_DAYS,
    ],
  },
  {
    fieldName: LoanProgramFieldName.CHAR_HAS_PREPAY_PENALTY,
    validation: ({ pendingChange, applicationData }) =>
      !(pendingChange?.newValue ?? applicationData[LoanProgramFieldName.CHAR_HAS_PREPAY_PENALTY]),
    dependents: [
      LoanProgramFieldName.PREPAY_FEATURE_TYPE,
      LoanProgramFieldName.PREPAY_MONTHS_IN_AFFECT,
      LoanProgramFieldName.PREPAY_CHARGE_TERM_MONTHS,
      LoanProgramFieldName.PREPAY_CHARGE_RATE,
      LoanProgramFieldName.PREPAY_MAX_AMOUNT,
      LoanProgramFieldName.PREPAY_DESCRIPTION,
    ],
  },
  {
    fieldName: LoanProgramFieldName.CHAR_INTEREST_ONLY,
    validation: ({ pendingChange, applicationData }) =>
      !(pendingChange?.newValue ?? applicationData[LoanProgramFieldName.CHAR_INTEREST_ONLY]),
    dependents: [LoanProgramFieldName.INTEREST_ONLY_PERIOD],
  },
  {
    fieldName: LoanProgramFieldName.CHAR_BALLOON_PAYMENT,
    validation: ({ pendingChange, applicationData }) =>
      !(pendingChange?.newValue ?? applicationData[LoanProgramFieldName.CHAR_BALLOON_PAYMENT]),
    dependents: [LoanProgramFieldName.BALLOON_TERM_MONTHS],
  },
  {
    fieldName: LoanProgramFieldName.CHAR_ASSUMABLE,
    validation: ({ pendingChange, applicationData }) =>
      !(pendingChange?.newValue ?? applicationData[LoanProgramFieldName.CHAR_ASSUMABLE]),
    dependents: [LoanProgramFieldName.ASSUMABLE_DETAILS],
  },
  {
    fieldName: LoanProgramFieldName.PROGRAM_LOAN_TYPE,
    validation: ({ pendingChange, applicationData }) =>
      (pendingChange?.newValue ?? applicationData[LoanProgramFieldName.PROGRAM_LOAN_TYPE]) !==
      LoanType.HELOC,
    dependents: [
      LoanProgramFieldName.ANNUAL_FEE,
      LoanProgramFieldName.MONTHLY_FEE,
      LoanProgramFieldName.INIT_DRAW_PERIOD,
      LoanProgramFieldName.INIT_DRAW_MIN_TYPE,
      LoanProgramFieldName.INIT_DRAW_MIN_PERCENT,
      LoanProgramFieldName.INIT_DRAW_MIN_AMOUNT,
      LoanProgramFieldName.INIT_DRAW_AMORT_TYPE,
      LoanProgramFieldName.INIT_DRAW_VAR_RATE_TYPE,
      LoanProgramFieldName.REPAY_PERIOD,
      LoanProgramFieldName.REPAY_TYPE,
    ],
  },
  {
    fieldName: LoanProgramFieldName.INIT_DRAW_MIN_TYPE,
    validation: ({ pendingChange, applicationData }) =>
      (pendingChange?.newValue ?? applicationData[LoanProgramFieldName.INIT_DRAW_MIN_TYPE]) !==
      MinimumInitialDrawType.LINE_LIMIT_PERCENTAGE,
    dependents: [LoanProgramFieldName.INIT_DRAW_MIN_PERCENT],
  },
  {
    fieldName: LoanProgramFieldName.INIT_DRAW_MIN_TYPE,
    validation: ({ pendingChange, applicationData }) =>
      (pendingChange?.newValue ?? applicationData[LoanProgramFieldName.INIT_DRAW_MIN_TYPE]) !==
      MinimumInitialDrawType.CURRENCY_AMOUNT,
    dependents: [LoanProgramFieldName.INIT_DRAW_MIN_AMOUNT],
  },
];

const getLoanRoleAssigneePendingChange = pendingChange =>
  Object.values(_.omit(pendingChange, ['isSeparateChange', 'isSeparateChangeAction']))[0];

// A checkbox renders unchecked for both null and false, so the server defaulting one from null to
// false on save is not a change the user can see (useForm treats a toggle's nil as false too).
const loanInfoBooleanToggleFieldsPendingChangeNormalizers = Object.values(loanInfoConfigFields)
  .filter(({ type }) => isBooleanToggleField(type))
  .map(({ nameInModel }) => ({
    fieldName: nameInModel,
    normalize: ({ pendingChange }) => {
      if (!checkBooleanPendingChangeVisibility(pendingChange)) {
        return;
      }

      return pendingChange;
    },
  }));

const loanAndLockFieldsPendingChangeNormalizers = [
  ...loanInfoBooleanToggleFieldsPendingChangeNormalizers,
  {
    fieldName: 'additionalMortgages',
    normalize: ({ pendingChange, entitySideData, applicationData }) => {
      const normalizedPendingChanges = Object.entries(pendingChange ?? {}).reduce(
        (acc, [fieldKey, additionalMortgagePendingChanges]) => {
          const filteredPendingChanges = filterOptionalVisiblePendingChanges({
            pendingChanges: additionalMortgagePendingChanges,
            optionalVisibleFieldsTriggers: loanAndLockOptionalVisibleFieldsTriggers,
            applicationData,
          });

          const normalizedPendingChanges = normalizePendingChanges({
            pendingChanges: filteredPendingChanges,
            fieldsPendingChangeNormalizers: loanAndLockFieldsPendingChangeNormalizers,
          });

          acc[fieldKey] = {
            ...normalizedPendingChanges,
            isSeparateChange: true,
          };

          return acc;
        },
        {},
      );

      return _.mapValues(
        fillPendingChangesWithActionType({
          newRecords: entitySideData.additionalMortgages?.newAddedAdditionalMortgages,
          newAddedInitialRecords: applicationData.initiallyNewAddedAdditionalMortgagesFieldsKeys,
          deletedRecords: [
            ...(entitySideData.additionalMortgages?.deletedAdditionalMortgages ?? []),
            ...(applicationData.initiallyDeletedAdditionalMortgagesFieldsKeys ?? []),
          ],
          pendingChanges: normalizedPendingChanges,
        }),
        pendingChanges => ({ ...pendingChanges, isSeparateChange: true }),
      );
    },
  },
  {
    fieldName: 'housingFinanceAgencyId',
    normalize: ({ pendingChange, housingFinanceAgenciesOptions }) => ({
      ...pendingChange,
      isOther:
        !!pendingChange?.newValue &&
        housingFinanceAgenciesOptions.find(option => option.value === pendingChange?.newValue)
          ?.isOther,
    }),
  },
  {
    fieldName: 'credits',
    normalize: ({ pendingChange, entitySideData, applicationData }) => {
      return normalizeTableEntitiesFields({
        entities: entitySideData.credits,
        initialEntitiesData: applicationData.credits || [],
        pendingChangesData: pendingChange,
        omittedFields: creditModifiedFieldsToExclude,
      });
    },
  },
  {
    fieldName: 'adjustments',
    normalize: ({ pendingChange, entitySideData, initialEntitySideData, applicationData }) => {
      return normalizeTableEntitiesFields({
        entities: entitySideData.adjustments,
        initialEntitiesData: initialEntitySideData.adjustments || applicationData.adjustments || [],
        pendingChangesData: pendingChange,
        omittedFields: adjustmentsModifiedFieldsToExclude,
      });
    },
  },
  {
    fieldName: 'isSubordinate',
    normalize: ({ pendingChange }) =>
      getConditionalObject(checkBooleanPendingChangeVisibility(pendingChange), pendingChange),
  },
  {
    fieldName: 'excludePayment',
    normalize: ({ pendingChange }) =>
      getConditionalObject(checkBooleanPendingChangeVisibility(pendingChange), pendingChange),
  },
  {
    fieldName: 'heloc',
    normalize: ({ pendingChange }) =>
      getConditionalObject(checkBooleanPendingChangeVisibility(pendingChange), pendingChange),
  },
  {
    fieldName: 'isAffordableLoan',
    normalize: ({ pendingChange }) =>
      getConditionalObject(checkBooleanPendingChangeVisibility(pendingChange), pendingChange),
  },
  {
    fieldName: 'monthsDeferred',
    normalize: ({ pendingChange }) => ({
      ...pendingChange,
      newValue: Number(pendingChange.newValue) || 0,
      initialValue: Number(pendingChange.initialValue) || 0,
    }),
  },
  {
    fieldName: 'loanTags',
    normalize: ({ pendingChange, entitySideData, applicationData }) =>
      _.mapValues(
        fillPendingChangesWithActionType({
          newRecords: entitySideData.newAddedLoanTags,
          newAddedInitialRecords: applicationData.initiallyNewAddedLoanTags,
          deletedRecords: [
            ...(entitySideData.deletedLoanTags ?? []),
            ...(applicationData.initiallyDeletedLoanTags ?? []),
          ],
          pendingChanges: pendingChange,
        }),
        pendingChanges => ({ ...pendingChanges, isSeparateChange: true }),
      ),
  },
  {
    fieldName: 'loanRoles',
    normalize: ({ pendingChange, entitySideData, applicationData }) => {
      const loanRolesById = _.keyBy(entitySideData.loanRoles, 'loanRoleId');
      const savedLoanRolesById = _.keyBy(applicationData.loanRoles, 'loanRoleId');

      const reassignedLoanRolesPendingChanges = entitySideData.reassignedLoanRoles?.reduce(
        (acc, loanRoleId) => {
          const { userName: newValue, roleName } = loanRolesById[loanRoleId];

          const pendingChange = {
            newValue,
          };

          const formattedRoleName = _.camelCase(roleName);

          acc[loanRoleId] = {
            [formattedRoleName]: pendingChange,
          };

          return acc;
        },
        {},
      );

      const mergedPendingChanges = mergePendingChanges(
        pendingChange,
        reassignedLoanRolesPendingChanges,
      );

      return _.mapValues(
        fillPendingChangesWithActionType({
          newRecords: entitySideData.newAssignedLoanRoles,
          newAddedInitialRecords: applicationData.initiallyNewAssignedLoanRoles,
          deletedRecords: [
            ...(entitySideData.unassignedLoanRoles ?? []),
            ...(applicationData.initiallyUnassignedLoanRoles ?? []),
          ],
          pendingChanges: mergedPendingChanges,
        }),
        (pendingChanges, loanRoleId) => {
          const assigneePendingChange = getLoanRoleAssigneePendingChange(pendingChanges);
          const { pendingChangesActionType } = pendingChanges;

          const isNewAdded =
            pendingChangesActionType === PendingChangesActionType.ADDED ||
            assigneePendingChange.actionType === HistoryPendingChangesActionType.ADDED;

          const loanRolePendingChange = {
            assignee: {
              ...assigneePendingChange,
              initialValue: isNewAdded
                ? null
                : (assigneePendingChange?.initialValue ?? savedLoanRolesById[loanRoleId]?.userName),
              newValue: loanRolesById[loanRoleId]?.userName,
            },
            ...getConditionalObject(pendingChangesActionType, {
              pendingChangesActionType,
            }),
          };

          return { ...loanRolePendingChange, isSeparateChange: true };
        },
      );
    },
  },
  {
    fieldName: 'productId',
    normalize: ({ pendingChange, entitySideData, applicationData }) => {
      const selectedProductId =
        entitySideData.selectedProgram?.productId || entitySideData.selectedProgram?.programId;

      if (!selectedProductId) {
        return pendingChange;
      }

      const initialProductId =
        pendingChange?.initialValue ??
        applicationData?.currentProductId ??
        applicationData?.currentProgramId;

      if (selectedProductId === initialProductId) {
        return;
      }

      return {
        ...pendingChange,
        initialValue: initialProductId,
        newValue: selectedProductId,
        serverNewValue: pendingChange?.newValue,
      };
    },
  },
  {
    fieldName: 'lockTerm',
    normalize: ({ pendingChange, entitySideData, applicationData }) => {
      const selectedLockTerm = entitySideData.selectedProgram?.lockTerm;

      if (_.isNil(selectedLockTerm)) {
        return pendingChange;
      }

      const initialLockTerm = pendingChange?.initialValue ?? applicationData?.currentLockTerm;

      if (selectedLockTerm === initialLockTerm) {
        return;
      }

      return {
        ...pendingChange,
        initialValue: initialLockTerm,
        newValue: selectedLockTerm,
        serverNewValue: pendingChange?.newValue,
      };
    },
  },
  {
    fieldName: 'finalBuyPrice',
    normalize: ({ pendingChange, entitySideData, applicationData }) => {
      const selectedFinalBuyPrice = entitySideData.selectedProgram?.finalBuyPrice;

      if (_.isNil(selectedFinalBuyPrice)) {
        return pendingChange;
      }

      const initialFinalBuyPrice =
        pendingChange?.initialValue ?? applicationData?.currentFinalBuyPrice;

      if (selectedFinalBuyPrice === initialFinalBuyPrice) {
        return;
      }

      return {
        ...pendingChange,
        initialValue: initialFinalBuyPrice,
        newValue: selectedFinalBuyPrice,
        serverNewValue: pendingChange?.newValue,
      };
    },
  },
  {
    fieldName: 'rate',
    normalize: ({ pendingChange, entitySideData, applicationData }) => {
      const selectedRate = entitySideData.selectedProgram?.rate;

      if (_.isNil(selectedRate)) {
        return pendingChange;
      }

      const initialRate = pendingChange?.initialValue ?? applicationData?.rate;

      if (selectedRate === initialRate) {
        return;
      }

      return {
        ...pendingChange,
        initialValue: initialRate,
        newValue: selectedRate,
        serverNewValue: pendingChange?.newValue,
      };
    },
  },
  {
    fieldName: 'principalAndInterest',
    normalize: ({ pendingChange, entitySideData, applicationData }) => {
      const selectedPrincipalAndInterest =
        entitySideData.selectedProgram?.paymentMonth ?? entitySideData.selectedProgram?.payment;

      if (
        _.isNil(selectedPrincipalAndInterest) ||
        selectedPrincipalAndInterest === TBD_AFTER_SAVE
      ) {
        return pendingChange;
      }

      const initialPrincipalAndInterest =
        pendingChange?.initialValue ?? applicationData?.currentPrincipalAndInterest;

      if (selectedPrincipalAndInterest === initialPrincipalAndInterest) {
        return;
      }

      return {
        ...pendingChange,
        initialValue: initialPrincipalAndInterest,
        newValue: selectedPrincipalAndInterest,
      };
    },
  },
];

const getSelectedProgramPendingChanges = entitySideData => {
  const { selectedProgram } = entitySideData;

  if (!selectedProgram) {
    return {};
  }

  const selectedPayment = selectedProgram.paymentMonth ?? selectedProgram.payment;

  return {
    ...getConditionalObject(selectedProgram.productId || selectedProgram.programId, {
      productId: {},
    }),
    ...getConditionalObject(!_.isNil(selectedProgram.lockTerm), { lockTerm: {} }),
    ...getConditionalObject(!_.isNil(selectedProgram.finalBuyPrice), { finalBuyPrice: {} }),
    ...getConditionalObject(!_.isNil(selectedProgram.rate), { rate: {} }),
    ...getConditionalObject(!_.isNil(selectedPayment) && selectedPayment !== TBD_AFTER_SAVE, {
      principalAndInterest: {},
    }),
  };
};

export const normalizeLoanAndLockPendingChangesAction = ({
  initialPendingChanges,
  formPendingChanges,
  applicationData,
  entitySideData,
  isPurchase,
  isAdditionalLoan,
  isHelocPurposePurchase,
  shouldShowLoanRolesSection,
  shouldShowLoanTagsSection,
  shouldShowProfitAndLossSection,
  shouldShowOBCustomFieldsSection,
  initialEntitySideData,
  housingFinanceAgenciesOptions,
  isLoanNoPpeEngineType,
  purchasePrice,
  loanFees,
  chattelContractAmount,
  isChattelRefinance,
}) => {
  const normalizedInitialPendingChanges = normalizeLoanAndLockInitialPendingChanges({
    applicationData,
    pendingChanges: initialPendingChanges,
    isPurchase,
    isAdditionalLoan,
    isHelocPurposePurchase,
    shouldShowLoanRolesSection,
    shouldShowLoanTagsSection,
    shouldShowProfitAndLossSection,
    shouldShowOBCustomFieldsSection,
  });

  const rebasedFormPendingChanges = rebaseDerivedLoanInfoPendingChanges({
    formPendingChanges,
    isPurchase,
    purchasePrice,
    loanFees,
    chattelContractAmount,
    isChattelRefinance,
  });

  const mergedPendingChanges = {
    ...getSelectedProgramPendingChanges(entitySideData),
    ...mergePendingChanges(normalizedInitialPendingChanges, rebasedFormPendingChanges),
  };

  const normalizedPendingChanges = normalizePendingChanges({
    pendingChanges: mergedPendingChanges,
    fieldsPendingChangeNormalizers: loanAndLockFieldsPendingChangeNormalizers,
    entitySideData,
    applicationData,
    initialEntitySideData,
    housingFinanceAgenciesOptions,
  });

  const filteredNormalizedPendingChanges = filterOptionalVisiblePendingChanges({
    pendingChanges: normalizedPendingChanges,
    optionalVisibleFieldsTriggers: loanAndLockOptionalVisibleFieldsTriggers,
    applicationData,
    isLoanNoPpeEngineType,
  });

  return filteredNormalizedPendingChanges;
};

const normalizePropertyData = ({
  data,
  isPurchase,
  isConstructionToPermanent,
  isRefinance,
  isAdditionalLoan,
  isHelocPurposePurchase,
}) => {
  const { propertyAddress, propertyAndOccupancy } = data;
  const state = propertyAddress.address.address.country?.state?.abbreviation ?? null;
  const isManufacturedHome = checkIsManufacturedHome(propertyAndOccupancy.propertyType?.type);
  const isNewConstructionVisible = isPurchase || (isRefinance && isConstructionToPermanent);

  return {
    isSubjectProperty: propertyAddress.address.type === 'SubjectPropertyAddressModel',
    street: propertyAddress.address.address.street,
    unit: propertyAddress.address.address.unit,
    city: propertyAddress.address.address.city,
    county: startCasePreserveSymbols(propertyAddress.address.address.county),
    state,
    zip: propertyAddress.address.address.country?.zip ?? null,
    longitude: propertyAddress.address.address.longitude,
    latitude: propertyAddress.address.address.latitude,
    isDeedRestricted: propertyAddress.isDeedRestricted,
    ...getConditionalObject(checkIsTexasState(state), {
      hasOweltyOfPartition: propertyAddress.hasOweltyOfPartition,
    }),
    mixedUseProperty: propertyAddress.mixedUseProperty,
    propertyRightsEstateHeld: propertyAndOccupancy.propertyRightsEstateHeld,
    ...getConditionalObject(
      propertyAndOccupancy.propertyRightsEstateHeld === PropertyRightsEstateHeld.LEASE_HOLD,
      {
        leaseholdExpiration: propertyAndOccupancy.leaseholdExpiration,
      },
    ),
    appraisedValue: propertyAndOccupancy.appraisedValue,
    occupancy: propertyAndOccupancy.occupancy?.type,
    propertyType: propertyAndOccupancy.propertyType?.type,
    attachmentType: propertyAndOccupancy.attachmentType?.type,
    status: propertyAndOccupancy.status,
    ...getConditionalObject(propertyAndOccupancy.propertyType?.type === PropertyType.MULTI_UNIT, {
      numberOfUnits: propertyAndOccupancy.propertyType?.numberOfUnits,
    }),
    ...getConditionalObject(propertyAddress.isDeedRestricted, {
      deedRestrictionType: propertyAddress.deedRestrictionType,
      deedRestrictionMonths: propertyAddress.deedRestrictionMonths,
    }),
    ...getConditionalObject(isPurchase, {
      propertyValue: propertyAndOccupancy.purchasePrice,
      totalSalesConcessionAmount: propertyAndOccupancy.totalSalesConcessionAmount,
      isRelocationLoan: propertyAndOccupancy.isRelocationLoan ?? null,
    }),
    ...getConditionalObject(isRefinance || (isAdditionalLoan && !isHelocPurposePurchase), {
      originalCost: propertyAndOccupancy.originalCost,
    }),
    ...getConditionalObject(isNewConstructionVisible, {
      isNewConstruction: propertyAddress.isNewConstruction,
    }),
    completionDate: propertyAddress.isNewConstruction ? propertyAddress.completionDate : null,
    ...getConditionalObject(isManufacturedHome, {
      manufacturedHomeType: propertyAndOccupancy.manufacturedHomeType,
    }),
    hasAffordableLegalRestrictions: propertyAndOccupancy.hasAffordableLegalRestrictions ?? null,
    ...getConditionalObject(propertyAndOccupancy.hasAffordableLegalRestrictions, {
      isConveyable: propertyAndOccupancy.isConveyable ?? null,
    }),
    affordableHomeProviderEin: propertyAndOccupancy.affordableHomeProviderEin ?? '',
    communityLandTrustEin: propertyAndOccupancy.communityLandTrustEin ?? '',
    reapiPropertyId: data.reapiPropertyId || null,
    isShortTermRental: propertyAndOccupancy.isShortTermRental ?? null,
    isCondotel: propertyAndOccupancy.isCondotel ?? null,
    locationType: propertyAndOccupancy.locationType ?? null,
  };
};

const normalizeTransactionDetailsData = ({ energyRelatedImprovements, transactionDetailInfo }) => ({
  ...energyRelatedImprovements,
  ...transactionDetailInfo,
  transactionDetail: transactionDetailInfo.transactionDetailType,
  transactionDetailClosingType: transactionDetailInfo.closingType,
  energyRelatedImprovementsIndicator:
    energyRelatedImprovements?.energyRelatedImprovementsIndicator ?? false,
});

const normalizeAgentAndSellerData = ({
  underwritingAgents,
  underwritingSellers,
  forSaleByOwner,
}) => {
  const normalizedAgents = underwritingAgents.reduce(
    (acc, { loanUnderwritingAgentId, ...data }) => {
      switch (data.type) {
        case TableDataType.SELLING_AGENT:
          acc.sellingAgents.push({ ...data, id: loanUnderwritingAgentId });
          break;

        case TableDataType.LISTING_AGENT:
          acc.listingAgents.push({ ...data, id: loanUnderwritingAgentId });
          break;

        default:
          break;
      }

      return acc;
    },
    {
      forSaleByOwner,
      sellingAgents: [],
      listingAgents: [],
    },
  );
  const normalizedSellers = underwritingSellers.map(({ loanUnderwritingSellerId, ...data }) => ({
    ...data,
    id: loanUnderwritingSellerId,
    type: TableDataType.SELLER,
    sellerName: getPersonFullName(data),
    fullAddress: getFormattedFullAddress({
      address: data,
    }),
  }));

  return {
    ...normalizedAgents,
    sellers: normalizedSellers,
  };
};

const coalesceExistingExpense = (existingValue, proposedValue) =>
  existingValue == null && Number(proposedValue) > 0 ? proposedValue : existingValue;

// The existing housing expense on a refinance/additional-loan was previously left blank for the user to retype,
// even though it mirrors the proposed expense. Default the empty existing fields from their proposed counterpart so
// they are populated when the modal opens; only fields with a direct scalar proposed source are filled (taxes and PITI
// are computed/persisted server-side), and an already-entered value is never replaced.
const prefillExistingPropertyExpensesFromProposed = (
  existingPropertyExpenses,
  proposedPropertyExpenses,
) => {
  const existing = existingPropertyExpenses ?? {};

  return {
    ...existing,
    principalAndInterest: coalesceExistingExpense(
      existing.principalAndInterest,
      proposedPropertyExpenses.principalAndInterest,
    ),
    mortgageInsurance: coalesceExistingExpense(
      existing.mortgageInsurance,
      proposedPropertyExpenses.pmi,
    ),
    homeownersInsurance: coalesceExistingExpense(
      existing.homeownersInsurance,
      proposedPropertyExpenses.monthlyHomeownersInsurance,
    ),
    homeownersAssociation: coalesceExistingExpense(
      existing.homeownersAssociation,
      proposedPropertyExpenses.associationFeesAmount,
    ),
  };
};

const normalizePropertyExpensesData = ({
  propertyExpenses,
  status,
  isExistingPropertyExpensesVisible,
}) => {
  const fixedPropertyExpenses = mapProposedPropertyExpensesFields(
    propertyExpenses?.proposedPropertyExpenses,
  );

  const {
    propertyTaxes,
    supplementalInsurances,
    otherExpenses,
    expenses,
    netMonthlyRentalIncome,
    principalAndInterest,
  } = fixedPropertyExpenses;

  return {
    ...propertyExpenses,
    proposedPropertyExpenses: {
      ...fixedPropertyExpenses,
      netMonthlyRentalIncome: roundToDecimals(netMonthlyRentalIncome),
      expenses: roundToDecimals(expenses),
      mortgagesPayment: principalAndInterest,
      propertyTaxes: propertyTaxes.map(item => ({
        ...item,
        id: item.applicationImpoundId,
        amountDueDates: item.propertyTaxPayments,
        monthlyAmount: item.annualAmount / NUMBER_MONTHS_IN_YEAR,
        overrodeMonthlyAmount: item.overrodeAnnualAmount / NUMBER_MONTHS_IN_YEAR,
      })),
      supplementalInsurances: supplementalInsurances.map(item => ({
        ...item,
        id: item.applicationImpoundId,
        monthlyAmount: item.annualAmount / NUMBER_MONTHS_IN_YEAR,
      })),
      otherExpenses,
      status,
    },
    ...(isExistingPropertyExpensesVisible && {
      existingPropertyExpenses: prefillExistingPropertyExpensesFromProposed(
        propertyExpenses?.existingPropertyExpenses,
        fixedPropertyExpenses,
      ),
    }),
  };
};

const normalizeVestingData = ({ vestingInfo, trustInfo }) => {
  if (!vestingInfo) {
    return {};
  }

  const { type, name, ...otherTrustInfo } = trustInfo || {};

  return {
    vestingDescription: vestingInfo.description,
    vestingTypeId: vestingInfo.vestingTypeId,
    mannerHeld: vestingInfo.mannerHeld,
    bplCompanyName: vestingInfo.bplCompanyName,
    isTrustInfoExist: !_.isEmpty(trustInfo),
    trustType: type,
    trustName: name,
    ...otherTrustInfo,
  };
};

export const normalizePropertyAndLoanApplicationData = ({
  data,
  isPurchase,
  isRefinance,
  isAdditionalLoan,
  isHelocPurposePurchase,
  initialPendingChanges,
}) => {
  const {
    appraisal,
    project,
    propertyExpenses,
    property,
    vestingInfo,
    trustInfo,
    transactionDetails,
  } = data;

  const isConstructionToPermanent =
    transactionDetails?.transactionDetailInfo?.transactionDetailType ===
    TransactionDetailType.CONSTRUCTION_CONVERSATION_OR_TO_PERMANENT;

  const normalizedPropertyData = normalizePropertyData({
    data: property,
    isPurchase,
    isConstructionToPermanent,
    isRefinance,
    isAdditionalLoan,
    isHelocPurposePurchase,
  });
  const normalizedVestingData = normalizeVestingData({
    vestingInfo,
    trustInfo,
  });
  const normalizedAgentAndSellerData = data.underwritingAgentsAndSellersData
    ? normalizeAgentAndSellerData(data.underwritingAgentsAndSellersData)
    : {};

  const deletedUnitsIds =
    initialPendingChanges.units?.reduce((acc, { propertyUnitId }) => {
      if (propertyUnitId?.actionType === HistoryPendingChangesActionType.DELETED) {
        acc.push(propertyUnitId.initialValue);
      }

      return acc;
    }, []) ?? [];

  const normalizedPropertyExpenses = normalizePropertyExpensesData({
    propertyExpenses,
    status: isRefinance ? PropertyStatusType.RETAINED : null,
    isExistingPropertyExpensesVisible: isRefinance || (isAdditionalLoan && !isHelocPurposePurchase),
  });
  const normalizedTransactionDetails = normalizeTransactionDetailsData(transactionDetails);

  return {
    ...normalizedPropertyData,
    ...normalizedVestingData,
    ...normalizedAgentAndSellerData,
    ...normalizedPropertyExpenses,
    ...normalizedTransactionDetails,
    countyFipsCode: data.countyFipsCode || initialPendingChanges?.countyFipsCode?.newValue || null,
    [APPRAISAL_ORDER_CONTACTS_KEY]: excludePendingDeletedOrderContacts({
      savedContacts: data[APPRAISAL_ORDER_CONTACTS_KEY] ?? [],
      pendingChanges: initialPendingChanges?.[APPRAISAL_ORDER_CONTACTS_KEY],
    }),
    appraisal: {
      ...appraisal,
      uniformCollateralDataPortal: {
        ...appraisal.uniformCollateralDataPortal,
        units: _.sortBy(
          appraisal.uniformCollateralDataPortal.units.filter(
            ({ propertyUnitId }) => !deletedUnitsIds.includes(propertyUnitId),
          ),
          'orderNumber',
        ).slice(0, normalizedPropertyData.numberOfUnits ?? 1),
      },
    },
    project,
  };
};

// Fields are prefixed with "lp" to avoid collisions with loan details fields
// that share the same names (e.g. loanTerm, amortTerm, amortizationType).
// Both sections coexist in the same form state, so the prefix keeps them distinct.
const normalizeLoanProgramData = ({
  loanCharacteristics = {},
  armTerms = {},
  index = {},
  prepaymentPenalty = {},
  initialDrawPeriod = {},
  repaymentPeriod = {},
  ...program
} = {}) => ({
  [LoanProgramFieldName.PROGRAM_CODE]: program.programCode,
  [LoanProgramFieldName.PROGRAM_LOAN_TYPE]: program.programLoanType,
  [LoanProgramFieldName.LOAN_TERM]: program.loanTerm,
  [LoanProgramFieldName.AMORT_TERM]: program.amortTerm,
  [LoanProgramFieldName.AMORTIZATION_TYPE]: program.amortizationType,
  [LoanProgramFieldName.INTEREST_ONLY_PERIOD]: program.interestOnlyPeriod,
  [LoanProgramFieldName.BALLOON_TERM_MONTHS]: program.balloonPaymentTermMonths,
  [LoanProgramFieldName.ARM_INITIAL_CAP]: armTerms.initialCap,
  [LoanProgramFieldName.ARM_ADJUSTMENT_CAP]: armTerms.adjustmentCap,
  [LoanProgramFieldName.ARM_SUBSEQUENT_CAP]: armTerms.subsequentCap,
  [LoanProgramFieldName.ARM_RATE_ADJ_PERIOD]: armTerms.rateAdjustmentPeriod,
  [LoanProgramFieldName.ARM_SUB_RATE_ADJ_PERIOD]: armTerms.subsequentRateAdjustmentPeriod,
  [LoanProgramFieldName.ARM_FLOOR_RATE]: armTerms.floorMinimumRate,
  [LoanProgramFieldName.ARM_CEILING_RATE]: armTerms.ceilingRate,
  [LoanProgramFieldName.ARM_ADJ_CHANGE_INDEX_RATE]: armTerms.adjustmentChangeIndexRate,
  [LoanProgramFieldName.INDEX_TYPE_ID]: index.indexTypeId,
  [LoanProgramFieldName.INDEX_MARGIN]: index.margin,
  [LoanProgramFieldName.INDEX_RATE]: index.rate,
  [LoanProgramFieldName.INDEX_PERIOD_DAYS]: index.periodDays,
  [LoanProgramFieldName.PREPAY_FEATURE_TYPE]: prepaymentPenalty.featureType,
  [LoanProgramFieldName.PREPAY_MONTHS_IN_AFFECT]: prepaymentPenalty.monthsInAffect,
  [LoanProgramFieldName.PREPAY_CHARGE_TERM_MONTHS]: prepaymentPenalty.penaltyChargeTermMonths,
  [LoanProgramFieldName.PREPAY_CHARGE_RATE]: prepaymentPenalty.penaltyChargeRatePercents,
  [LoanProgramFieldName.PREPAY_MAX_AMOUNT]: prepaymentPenalty.maxPenaltyAmount,
  [LoanProgramFieldName.PREPAY_DESCRIPTION]: prepaymentPenalty.prepaymentDescription,
  [LoanProgramFieldName.ANNUAL_FEE]: program.annualFee,
  [LoanProgramFieldName.MONTHLY_FEE]: program.monthlyFee,
  [LoanProgramFieldName.INIT_DRAW_PERIOD]: initialDrawPeriod.periodMonths,
  [LoanProgramFieldName.INIT_DRAW_MIN_TYPE]: initialDrawPeriod.minimumInitialDrawType,
  [LoanProgramFieldName.INIT_DRAW_MIN_PERCENT]: initialDrawPeriod.minimumInitialDrawPercent,
  [LoanProgramFieldName.INIT_DRAW_MIN_AMOUNT]: initialDrawPeriod.minimumInitialDrawAmount,
  [LoanProgramFieldName.INIT_DRAW_AMORT_TYPE]: initialDrawPeriod.amortizationType,
  [LoanProgramFieldName.INIT_DRAW_VAR_RATE_TYPE]:
    initialDrawPeriod.rateType ?? initialDrawPeriod.type,
  [LoanProgramFieldName.INIT_DRAW_VARIABLE_RATE]: initialDrawPeriod.variableRate ?? null,
  [LoanProgramFieldName.REPAY_PERIOD]: repaymentPeriod.periodMonths,
  [LoanProgramFieldName.REPAY_TYPE]: repaymentPeriod.rateType ?? repaymentPeriod.type,
  [LoanProgramFieldName.REPAY_VARIABLE_RATE]: repaymentPeriod.variableRate ?? null,
  [LoanProgramFieldName.CHAR_ARM]: !!loanCharacteristics.arm,
  [LoanProgramFieldName.CHAR_ASSUMABLE]: !!loanCharacteristics.assumable,
  [LoanProgramFieldName.CHAR_CONFORMING]: !!loanCharacteristics.conforming,
  [LoanProgramFieldName.CHAR_COUNSELING_REQUIRED]: !!loanCharacteristics.counselingRequired,
  [LoanProgramFieldName.CHAR_INTEREST_ONLY]: !!loanCharacteristics.interestOnly,
  [LoanProgramFieldName.CHAR_HAS_PREPAY_PENALTY]: !!loanCharacteristics.hasPrepaymentPenalty,
  [LoanProgramFieldName.CHAR_RUN_AUS]: !!loanCharacteristics.runAus,
  [LoanProgramFieldName.CHAR_AFFORDABLE_LENDING]: !!loanCharacteristics.affordableLending,
  [LoanProgramFieldName.CHAR_TEXAS_CASH_OUT]: !!loanCharacteristics.texasCashOut,
  [LoanProgramFieldName.CHAR_DEMAND_FEATURE]: !!loanCharacteristics.demandFeature,
  [LoanProgramFieldName.CHAR_NEG_AMORT]: !!loanCharacteristics.negativeAmortization,
  [LoanProgramFieldName.CHAR_BALLOON_PAYMENT]: !!loanCharacteristics.balloonPayment,
  [LoanProgramFieldName.CHAR_BRIDGE_LOAN]: !!loanCharacteristics.bridgeLoan,
  [LoanProgramFieldName.CHAR_CONSTRUCTION_ONLY]: !!loanCharacteristics.constructionOnly,
  [LoanProgramFieldName.CHAR_MORE_THAN_2_PAYMENTS]:
    !!loanCharacteristics.moreThan2PaymentsCollected,
  [LoanProgramFieldName.CHAR_INTEREST_INCREASED]:
    !!loanCharacteristics.interestIncreasedAfterDefault,
  [LoanProgramFieldName.CHAR_DEMAND_CLAUSE]: !!loanCharacteristics.demandClause,
  [LoanProgramFieldName.CHAR_LENDER_PAID_MI]: !!loanCharacteristics.lenderPaidMI,
  [LoanProgramFieldName.ASSUMABLE_DETAILS]: program.assumableDetailsGroup?.assumableDetails,
});

const normalizeLoanInfoTabApplicationData = ({ data, loanProgram }) => {
  const { program = {}, details, additionalInformation, documentVerificationTypes } = data;

  const escrowSettings = _.cloneDeep(details.escrowSettings);

  if (escrowSettings?.insuranceEscrowSettings?.insuranceImpoundsEscrowed) {
    (escrowSettings.insuranceEscrowSettings.insuranceImpoundsEscrowed || []).forEach(
      ({ applicationInsuranceId, isEscrowed }) => {
        _.set(escrowSettings.insuranceEscrowSettings, applicationInsuranceId, {
          isEscrowed,
        });
      },
    );
  }

  return {
    // program details
    currentProgramId: program.programId ?? program.productId,
    programName: program.name,
    rate: program.rate,
    loanPeriod: program.loanPeriod,
    mortgageType: program.mortgageType,
    amortizationType: program.amortizationType,
    amortizationTerm: program.amortizationTerm,
    // loan program editable fields (base from LP record, overridden by effective values)
    ...normalizeLoanProgramData(loanProgram ?? undefined),
    ...getConditionalObject(!_.isNil(program.loanPeriod), {
      [LoanProgramFieldName.LOAN_TERM]: program.loanPeriod,
    }),
    ...getConditionalObject(!_.isNil(program.amortizationTerm), {
      [LoanProgramFieldName.AMORT_TERM]: program.amortizationTerm,
    }),
    ...getConditionalObject(!_.isNil(program.mortgageType), {
      [LoanProgramFieldName.PROGRAM_LOAN_TYPE]: program.mortgageType,
    }),
    ...getConditionalObject(!_.isNil(program.amortizationType), {
      [LoanProgramFieldName.AMORTIZATION_TYPE]: program.amortizationType,
    }),
    // loan details
    baseLoanAmount: details.baseLoanAmount,
    totalLoanAmount: details.totalLoanAmount,
    // downPaymentValue ($) is persisted via the loan-info save endpoint; downPaymentPercent (%) is
    // never sent -- it's derived from downPaymentValue on mount (see LoanInfo.js's sync effect).
    downPaymentValue: details.downPaymentValue ?? null,
    loanPurpose: details.purposeType?.type,
    refinancePurpose: details.purposeType?.refinancePurpose,
    cashOutAmount: details.purposeType?.cashOutAmount,
    isManualCashoutAmount: details.purposeType?.isManualCashoutAmount,
    additionalLoanSecondMortgageType: details.purposeType?.additionalLoanSecondMortgageType,
    additionalLoanDrawAmount:
      details.purposeType?.additionalLoanDrawAmount ??
      (program.mortgageType === LoanType.HELOC ? details.baseLoanAmount : undefined),
    helocPurpose: details.purposeType?.helocPurpose,
    tangibleNetBenefitReason: details.purposeType?.tangibleNetBenefit?.reason,
    tangibleNetBenefitCurrentInterestRate:
      details.purposeType?.tangibleNetBenefit?.currentInterestRate,
    tangibleNetBenefitCurrentAmortizationPeriodMonths:
      details.purposeType?.tangibleNetBenefit?.currentAmortizationPeriodMonths,
    tangibleNetBenefitDescription: details.purposeType?.tangibleNetBenefit?.description,
    requireEscrow: details.requireEscrow,
    escrowSettings,
    incomeDocumentationType: details.incomeDocumentationType ?? null,
    debtServiceCoverageRatio: details.debtServiceCoverageRatio ?? null,
    prepaymentPenaltyTermMonths: details.prepaymentPenaltyTermMonths ?? null,
    prepaymentPenaltyStructure: details.prepaymentPenaltyStructure ?? null,
    lienPriority: details.lienPriority,
    buyDown: details.buyDown?.buyDownId,
    vaTotalEntitlement: details.vaTotalEntitlement,
    miProductType: details.miProductType ?? MiProductType.MONTHLY,
    miPaymentType: details.miPaymentType ?? MiPaymentType.BORROWER_PAID,
    miRefundable: !!details.miRefundable,
    miRenewalType: details.miRenewalType ?? MiRenewalType.CONSTANT,
    miFinanced: !!details.miFinanced,
    miUpfrontPremiumPercent: details.miUpfrontPremiumPercent ?? null,
    miCompanyType: details.miCompanyType,
    miCompanyOtherDescription: details.miCompanyOtherDescription,
    miApplicationType: details.miApplicationType,
    miOrderStatus: details.miOrderStatus,
    miDecisionType: details.miDecisionType,
    miOrderedDate: details.miOrderedDate,
    miDecisionDate: details.miDecisionDate,
    miActivatedDate: details.miActivatedDate,
    miCertificateIdentifier: details.miCertificateIdentifier,
    miCertificateExpirationDate: details.miCertificateExpirationDate,
    miQuoteIdentifier: details.miQuoteIdentifier,
    miQuoteExpirationDate: details.miQuoteExpirationDate,
    miInitialPremiumRatePercent: details.miInitialPremiumRatePercent,
    miInitialPremiumTermMonths: details.miInitialPremiumTermMonths,
    miRenewalPremiumRatePercent: details.miRenewalPremiumRatePercent,
    miRenewalPremiumTermMonths: details.miRenewalPremiumTermMonths,
    miUpfrontPremiumAmount: details.miUpfrontPremiumAmount,
    miPremiumTaxAmount: details.miPremiumTaxAmount,
    // additional loan information
    areaMedianIncome: additionalInformation.areaMedianIncome,
    investorCodes: additionalInformation.investorCodes,
    leadSource:
      additionalInformation.leadSource.type === 'Empty'
        ? null
        : additionalInformation.leadSource.leadSourceId,
    loanProgramIdentifiers: additionalInformation.loanProgramIdentifiers,
    usdaEligible: !!additionalInformation.usdaEligible,
    ...getConditionalObject(!!additionalInformation.loanWorkflow, {
      loanWorkflow: additionalInformation.loanWorkflow,
    }),
    nationalHousingActSection: additionalInformation.nationalHousingActSection,
    fha203kConsultantId: additionalInformation.fha203kConsultantId,
    fhaBorrowerCertification: additionalInformation.fhaBorrowerCertification,
    agencyCaseNumber: additionalInformation.agencyCaseNumber,
    agencyCaseNumberIssueDate: additionalInformation.agencyCaseNumberIssueDate,
    minimumResidualIncome: additionalInformation.minimumResidualIncome,
    governmentRefinancePurpose: additionalInformation.governmentRefinancePurpose,
    housingFinanceAgencyId: additionalInformation.hfaInfo?.id,
    housingFinanceAgencyOtherDescription: additionalInformation.hfaInfo?.otherDescription,
    // HUD office info (read-only, auto-populated from subject property)
    hudFieldOfficeCode: additionalInformation.hudFieldOfficeCode,
    hudFieldOfficeName: additionalInformation.hudFieldOfficeName,
    hudHocCode: additionalInformation.hudHocCode,
    hudHocName: additionalInformation.hudHocName,
    fhaInspectorIdentifier: additionalInformation.fhaInspectorIdentifier,
    ...replaceEmptyWithNull({
      documentVerificationAssetType: documentVerificationTypes?.asset,
      documentVerificationEmploymentType: documentVerificationTypes?.employment,
      documentVerificationIncomeType: documentVerificationTypes?.income,
    }),
  };
};

const normalizeCreditsTabApplicationData = ({ data }) => {
  return data.map(({ id, creditType, amount }) => ({
    id,
    description: creditType.description,
    purchaseCreditType: creditType.purchaseCreditType,
    sourceType: creditType.sourceType?.type,
    otherDescription: creditType.sourceType?.description,
    creditType: creditType.type,
    amount,
  }));
};

const normalizeAdditionalMortgagesApplicationData = ({ data, initialPendingChanges }) =>
  data.reduce(
    (acc, mortgage, index) => {
      const actionType = initialPendingChanges.additionalMortgages?.[index]?.id?.actionType;
      const fieldKey = getAdditionalMortgageKey(index);

      if (actionType === HistoryPendingChangesActionType.DELETED) {
        acc.initiallyDeletedAdditionalMortgagesFieldsKeys.push(fieldKey);
      }

      if (actionType === HistoryPendingChangesActionType.ADDED) {
        acc.initiallyNewAddedAdditionalMortgagesFieldsKeys.push(fieldKey);
      }

      acc.additionalMortgages[fieldKey] = mortgage;

      return acc;
    },
    {
      additionalMortgages: {},
      initiallyNewAddedAdditionalMortgagesFieldsKeys: [],
      initiallyDeletedAdditionalMortgagesFieldsKeys: [],
    },
  );

const normalizeLoanTagsApplicationData = ({ data, initialPendingChanges }) =>
  sortArrayByDate(data, 'assignedDate', 'desc').reduce(
    (acc, loanTag) => {
      const { tagId } = loanTag;

      const actionType = Object.values(
        _.omit(initialPendingChanges.loanTags?.[tagId], 'isSeparateChange'),
      )[0]?.actionType;

      const isInitiallyDeleted = actionType === HistoryPendingChangesActionType.DELETED;
      const isInitiallyNewAdded = actionType === HistoryPendingChangesActionType.ADDED;

      if (isInitiallyDeleted) {
        acc.initiallyDeletedLoanTags.push(tagId);
      }

      if (isInitiallyNewAdded) {
        acc.initiallyNewAddedLoanTags.push(tagId);
      }

      const formattedLoanTagData = {
        ...loanTag,
        isDeleted: false,
        isNewAdded: false,
        isInitiallyDeleted,
        isInitiallyNewAdded,
      };

      acc.loanTags.push(formattedLoanTagData);

      return acc;
    },
    {
      loanTags: [],
      initiallyNewAddedLoanTags: [],
      initiallyDeletedLoanTags: [],
    },
  );

const normalizeLoanRolesApplicationData = ({ data, initialPendingChanges }) =>
  sortArrayByDate(data, 'assignedDate', 'desc').reduce(
    (acc, loanRole) => {
      const { loanRoleId } = loanRole;

      const { actionType, newValue, initialValue } =
        getLoanRoleAssigneePendingChange(initialPendingChanges.loanRoles?.[loanRoleId]) ?? {};

      const isPendingChangeHasDifference = checkIsPendingChangeHasDifference({
        newValue,
        initialValue,
      });

      const isInitiallyUnassigned =
        isPendingChangeHasDifference &&
        (actionType === HistoryPendingChangesActionType.DELETED || _.isNull(newValue));
      const isInitiallyNewAssigned =
        isPendingChangeHasDifference &&
        (actionType === HistoryPendingChangesActionType.ADDED || (!!newValue && !initialValue));
      const isInitiallyReassigned =
        isPendingChangeHasDifference &&
        actionType === HistoryPendingChangesActionType.MODIFIED &&
        !!newValue;

      if (isInitiallyUnassigned) {
        acc.initiallyUnassignedLoanRoles.push(loanRoleId);
      }

      if (isInitiallyNewAssigned) {
        acc.initiallyNewAssignedLoanRoles.push(loanRoleId);
      }

      const formattedLoanRoleData = {
        ...loanRole,
        isUnassigned: false,
        isNewAssigned: false,
        isReassigned: false,
        isInitiallyUnassigned,
        isInitiallyNewAssigned,
        isInitiallyReassigned,
      };

      acc.loanRoles.push(formattedLoanRoleData);

      return acc;
    },
    {
      loanRoles: [],
      initiallyNewAssignedLoanRoles: [],
      initiallyUnassignedLoanRoles: [],
    },
  );

const normalizeAdjustmentsApplicationData = ({ data }) => {
  const formattedLockExtensions = data.lockExtensions.map(item => ({
    ...item,
    id: item.lockExtensionId,
  }));

  const formattedFloatDown = data.floatDowns.map(item => ({
    ...item,
    id: item.llpaTypeId,
  }));

  const formattedAutoAdjustments = data.autoAdjustments.map(item => ({
    ...item,
    id: item.criteriaId,
  }));

  const formattedCustomAdjustments = data.customAdjustments.map(item => ({
    ...item,
    id: item.customAdjustmentTypeId,
  }));

  return [
    ...formattedLockExtensions,
    ...formattedFloatDown,
    ...formattedAutoAdjustments,
    ...formattedCustomAdjustments,
  ];
};

const normalizeMarkupsApplicationData = ({ data }) => {
  return data.map(markup => ({
    ...markup,
    id: markup.level,
    finalValue: `${formatNumberToCurrencyString(markup.finalValue ?? 0, currencyOptions)} (${(
      markup.percent ?? 0
    ).toFixed(RATE_DECIMAL_SCALE)}%)`,
  }));
};

export const normalizeLoanAndLockApplicationData = ({
  data,
  initialPendingChanges,
  isPurchase,
  isAdditionalLoan,
  isHelocPurposePurchase,
  shouldShowLoanRolesSection,
  shouldShowLoanTagsSection,
  shouldShowProfitAndLossSection,
  shouldShowOBCustomFieldsSection,
}) => {
  const {
    additionalMortgages,
    loan,
    loanProgram,
    credits,
    loanTagAssignments,
    loanRoleAssignments,
    lock,
    profitAndLoss,
    sellSideLock,
    chattelDetails,
  } = data;

  const loanInfoTabData = normalizeLoanInfoTabApplicationData({
    data: loan,
    loanProgram,
  });

  // For auto-calculated read-only HUD fields, use pending change values as fallback
  // since they may only exist in pending changes before being committed to the entity
  const hudPendingChangeFields = [
    'hudFieldOfficeCode',
    'hudFieldOfficeName',
    'hudHocCode',
    'hudHocName',
    'countyFipsCode',
  ];

  hudPendingChangeFields.forEach(field => {
    if (!loanInfoTabData[field] && initialPendingChanges?.[field]?.newValue) {
      loanInfoTabData[field] = initialPendingChanges[field].newValue;
    }
  });

  const additionalMortgagesTabData =
    isPurchase || (isAdditionalLoan && isHelocPurposePurchase)
      ? normalizeAdditionalMortgagesApplicationData({
          data: additionalMortgages,
          initialPendingChanges,
        })
      : null;

  const creditsData = normalizeCreditsTabApplicationData({
    data: credits,
  });

  const loanTagsTabData = shouldShowLoanTagsSection
    ? normalizeLoanTagsApplicationData({
        data: loanTagAssignments,
        initialPendingChanges,
      })
    : null;

  const adjustmentsData = normalizeAdjustmentsApplicationData({
    data: lock.adjustments,
  });

  const markupsData = normalizeMarkupsApplicationData({ data: lock.markups ?? [] });

  const loanRolesTabData = shouldShowLoanRolesSection
    ? normalizeLoanRolesApplicationData({
        data: loanRoleAssignments,
        initialPendingChanges,
      })
    : null;

  return {
    ...loanInfoTabData,
    purposeOfChattel: chattelDetails?.purposeOfChattel ?? null,
    ...additionalMortgagesTabData,
    ...loanTagsTabData,
    ...loanRolesTabData,
    credits: creditsData,
    adjustments: adjustmentsData,
    markups: markupsData,
    profitAndLoss: shouldShowProfitAndLossSection ? profitAndLoss : null,
    sellSideLock: shouldShowProfitAndLossSection ? sellSideLock : null,
    obCustomFields: shouldShowOBCustomFieldsSection
      ? loan.optimalBlueCustomFieldsData.customFields.reduce((acc, i) => {
          acc[`${i.columnName}${i.name}${i.submissionForm}`] = i.fieldValue;

          return acc;
        }, {})
      : null,
    currentProductId: lock.productId,
    currentLockTerm: lock.lockTerm,
    currentFinalBuyPrice: lock.finalBuyPrice,
    currentPrincipalAndInterest: lock.principalAndInterest,
  };
};

const normalizeAppraisalDataToSend = (form, applicationData, loanType) => {
  const isDataToNormalize =
    form.appraisal ||
    ((form.numberOfUnits?.value !== undefined || form.propertyType) &&
      form.numberOfUnits?.value !== applicationData.numberOfUnits);

  if (!isDataToNormalize) {
    return null;
  }

  const { appraisal = applicationData.appraisal } = form;

  const isFieldValueExtractionNeeded = !!form.appraisal;
  const isMortgageTypeVA = loanType === LoanType.VA;

  const appraisalParticipants = {
    appraisalManagementCompanyName: appraisal.appraisalManagementCompanyName,
    appraiserName: appraisal.appraiserName,
    appraiserLicenseNumber: appraisal.appraiserLicenseNumber,
    appraisalCompanyName: appraisal.appraisalCompanyName,
    supervisoryAppraiserName: appraisal.supervisoryAppraiserName,
  };
  // The API maps a present appraisalParticipants node as a full overwrite of all its columns
  // (nulls included). Until the Loans read DTO returns these fields (DEV-6541), the form carries
  // none of them, so the node must be omitted entirely rather than sent as explicit nulls.
  const hasAppraisalParticipants = Object.values(appraisalParticipants).some(
    value => value !== undefined,
  );

  const extractValueIfNeeded = field => (isFieldValueExtractionNeeded ? field?.value : field);

  const normalizePropertyUnits = () => {
    const numberOfUnits =
      (form.propertyType !== undefined
        ? form.numberOfUnits?.value
        : applicationData.numberOfUnits) ?? 1;
    const applicationUnits = appraisal.uniformCollateralDataPortal.units;

    return Array.from({ length: numberOfUnits }, (_, index) => {
      const { propertyUnitId = null, orderNumber = index + 1 } =
        applicationData.appraisal.uniformCollateralDataPortal.units[index] ?? {};

      const unitValues =
        applicationUnits[index] ??
        unitFields.reduce((acc, fieldName) => {
          acc[fieldName] = null;

          return acc;
        }, {});

      return {
        orderNumber,
        propertyUnitId,
        ...(applicationUnits[index] ? replaceEmptyWithNull(unitValues) : unitValues),
      };
    });
  };

  return {
    appraisalProductTypeId: extractValueIfNeeded(appraisal.appraisalProductTypeId) ?? null,
    uniformCollateralDataPortal: {
      appraisalDocumentId: appraisal.uniformCollateralDataPortal.appraisalDocumentId,
      appraisalUploadStatus: extractValueIfNeeded(
        appraisal.uniformCollateralDataPortal.appraisalUploadStatus,
      ),
      dataTransmittalDate: appraisal.uniformCollateralDataPortal.appraisalDataTransmittalDate
        ? dayjs(appraisal.uniformCollateralDataPortal.appraisalDataTransmittalDate).format(
            COMMON_REQUEST_DATE_FORMAT,
          )
        : null,
      propertyUnits: normalizePropertyUnits(),
    },
    neighbourhoodAndSite: replaceEmptyWithNull({
      ...appraisal.neighbourhoodAndSite,
      yearBuilt: appraisal.neighbourhoodAndSite.yearBuilt
        ? dayjs(appraisal.neighbourhoodAndSite.yearBuilt).format('YYYY')
        : null,
      locationType: extractValueIfNeeded(appraisal.neighbourhoodAndSite.locationType),
      lotAcquiredDate:
        appraisal.neighbourhoodAndSite.lotAcquiredDate &&
        dayjs(appraisal.neighbourhoodAndSite.lotAcquiredDate).format(COMMON_REQUEST_DATE_FORMAT),
      surveyDate:
        appraisal.neighbourhoodAndSite.surveyDate &&
        dayjs(appraisal.neighbourhoodAndSite.surveyDate).format(COMMON_REQUEST_DATE_FORMAT),
    }),
    propertyDetails: replaceEmptyWithNull({
      ...getConditionalObject(isMortgageTypeVA, {
        propertySquareFootage: appraisal.propertyDetails?.propertySquareFootage,
      }),
    }),
    ...getConditionalObject(hasAppraisalParticipants, () => ({
      appraisalParticipants: replaceEmptyWithNull(appraisalParticipants),
    })),
  };
};

const checkIsFormRentCollected = form =>
  checkIsAmountOfRentCollectedActive({
    occupancy: form.occupancy?.value,
    propertyType: form.propertyType?.value,
  });

const normalizePropertyExpensesDataToSend = (form, entitySideData) => {
  const { proposedPropertyExpenses = {} } = form;

  if (!('principalAndInterest' in proposedPropertyExpenses)) {
    return null;
  }

  const isAmountOfRentCollectedActive = checkIsFormRentCollected(form);

  const propertyTaxes = prepareNestedProposedPropertyExpensesToSend(entitySideData.propertyTaxes);
  const supplementalInsurances = prepareNestedProposedPropertyExpensesToSend(
    entitySideData.supplementalInsurances,
  );

  const otherExpenses = (entitySideData.otherExpenses ?? []).reduce(
    (acc, { id, isDeleted, isAdded, pendingChangesActionType, ...rest }) => {
      if (isDeleted || pendingChangesActionType === PendingChangesActionType.DELETED) {
        return acc;
      }

      acc.push({ ...(!isAdded && { id }), ...rest });

      return acc;
    },
    [],
  );

  return {
    propertyExpenses: {
      proposedPropertyExpenses: replaceEmptyWithNull({
        ..._.omit(proposedPropertyExpenses, [
          // Read-only, backend-computed display value surfaced for the Proposed P&I qualifying tooltip —
          // never send it back on save.
          'qualifyingPrincipalAndInterest',
          ...getConditionalArray(!isAmountOfRentCollectedActive, [
            'netMonthlyRentalIncome',
            'expenses',
            'rentCalculatedBasedOn',
            'monthlyRentalIncome',
          ]),
        ]),
        monthlyPmi: proposedPropertyExpenses.pmi,
        monthlyPmiManualUpdate: proposedPropertyExpenses.pmiManualUpdate,
        propertyTaxes,
        supplementalInsurances,
        otherExpenses,
        // Send a date-only value: a raw dayjs serializes as a UTC timestamp, which shifts the calendar
        // day (and the backend-derived escrow months) for any client timezone offset from UTC.
        homeownersInsurancePolicyDueDate: proposedPropertyExpenses.homeownersInsurancePolicyDueDate
          ? dayjs(proposedPropertyExpenses.homeownersInsurancePolicyDueDate).format(
              COMMON_REQUEST_DATE_FORMAT,
            )
          : null,
      }),
      ...(form.existingPropertyExpenses && {
        existingPropertyExpenses: replaceEmptyWithNull(form.existingPropertyExpenses),
      }),
    },
  };
};

// A create/update/delete set with no operation changes nothing, so it compares equal to none at all.
const getImpoundChangesOrNull = changes =>
  _.isEmpty(changes?.create) && _.isEmpty(changes?.update) && _.isEmpty(changes?.delete)
    ? null
    : changes;

// Flattens a whole-object expenses body into the patch's keys. The whole-object save drops the rent fields while
// rent is not collected and the backend then clears them, so the patch carries them as cleared.
const toPropertyExpensesPatchSource = ({ propertyExpenses }, isRentCollected) => {
  const { proposedPropertyExpenses, existingPropertyExpenses } = propertyExpenses;

  return {
    ...proposedPropertyExpenses,
    ...getConditionalObject(!isRentCollected, () =>
      Object.fromEntries(PROPERTY_EXPENSES_RENT_KEYS.map(key => [key, null])),
    ),
    propertyTaxes: getImpoundChangesOrNull(proposedPropertyExpenses.propertyTaxes),
    supplementalInsurances: getImpoundChangesOrNull(
      proposedPropertyExpenses.supplementalInsurances,
    ),
    existingPropertyExpenses: existingPropertyExpenses ?? null,
  };
};

// The property-info save sends only the expenses that changed since the modal opened, as Option patches: every
// author's pending changes are the backend's diff baseline, so re-sending an untouched expense would be recorded
// as this user's change and revert another author's pending value. A rent value the form derived (or cleared once
// rent stopped being collected) is sent as recalculated unless the user edited it.
const getPropertyExpensesRequestData = ({
  form,
  initialForm,
  entitySideData,
  initialEntitySideData,
  userEditedFieldNames,
}) => {
  const current = normalizePropertyExpensesDataToSend(form, entitySideData);

  if (!current || !initialForm || !initialEntitySideData) {
    return current;
  }

  const isRentCollected = checkIsFormRentCollected(form);
  const baseline = normalizePropertyExpensesDataToSend(initialForm, initialEntitySideData);

  return {
    propertyExpenses: {
      patch: getPatch({
        current: toPropertyExpensesPatchSource(current, isRentCollected),
        baseline:
          baseline &&
          toPropertyExpensesPatchSource(baseline, checkIsFormRentCollected(initialForm)),
        keys: PROPERTY_EXPENSES_PATCH_KEYS,
        isRecalculatedKey: key =>
          !!userEditedFieldNames &&
          !userEditedFieldNames.has(`proposedPropertyExpenses.${key}`) &&
          (PROPERTY_EXPENSES_DERIVED_KEYS.includes(key) ||
            (!isRentCollected && PROPERTY_EXPENSES_RENT_KEYS.includes(key))),
      }),
    },
  };
};

const normalizeTransactionDetailsDataToSend = form => {
  if (!_.has(form, 'transactionDetail')) {
    return null;
  }

  const {
    transactionDetail,
    constructionOrImprovementCosts,
    transactionDetailClosingType,
    energyImprovementAmount,
    energyRelatedImprovementsIndicator,
    amountOfEnergyEfficiencyMortgage,
    listOfEnergyImprovements,
  } = form;

  const transactionDetailValue = transactionDetail?.value;
  const isConstructionOrImprovementCostsVisible =
    constructionOrImprovementCostsVisibility.includes(transactionDetailValue) &&
    constructionOrImprovementCosts;
  const isClosingTypeVisible = closingTypeVisibility.includes(transactionDetailValue);

  return {
    ...getConditionalObject(transactionDetailValue, () => ({
      transactionDetailInfo: {
        type: transactionDetailValue,
        closingType: getConditionalValue(isClosingTypeVisible, transactionDetailClosingType?.value),
      },
      constructionOrImprovementCosts: getConditionalValue(
        isConstructionOrImprovementCostsVisible,
        constructionOrImprovementCosts,
      ),
    })),
    energyRelatedImprovements: {
      ...replaceEmptyWithNull({
        energyImprovementAmount,
        amountOfEnergyEfficiencyMortgage,
        listOfEnergyImprovements,
      }),
      energyRelatedImprovementsIndicator,
    },
  };
};

// Null keeps the stored value. A condotel flag only applies to a condo, so it is cleared when the
// property is no longer one.
const getNonQmPropertyRequestData = ({ form, applicationData, isCondo }) => ({
  isShortTermRental: form.isShortTermRental ?? null,
  isCondotel: isCondo
    ? (form.isCondotel ?? null)
    : getConditionalValue(!!applicationData?.isCondotel, false),
  locationType: getFormOptionValue(form.locationType),
});

const getPropertyRequestData = ({ form, applicationData }) => {
  const isMixedUseVisible = checkIsMixedUseVisible({
    occupancy: form.occupancy?.value,
    propertyType: form.propertyType?.value,
  });

  const isManufacturedHome = checkIsManufacturedHome(form.propertyType?.value);
  const isCondo = form.propertyType?.value === PropertyType.CONDO;

  return {
    property: {
      address: {
        county: form.county?.value,
        state: form.state?.value,
        zip: form.zip,
        city: form.city,
        street: form.street,
        unit: form.unit,
        longitude: form.longitude,
        latitude: form.latitude,
      },
      isDeedRestricted: form.isDeedRestricted,
      ...getConditionalObject(checkIsTexasState(form.state?.value), {
        hasOweltyOfPartition: form.hasOweltyOfPartition,
      }),
      ...getConditionalObject(isMixedUseVisible, {
        mixedUseProperty: form.mixedUseProperty,
      }),
      deedRestrictionType: form.deedRestrictionType?.value,
      deedRestrictionMonths: form.deedRestrictionMonths,
      isNewConstruction: form.isNewConstruction,
      ...getConditionalObject(form.isNewConstruction, () => ({
        completionDate: dayjs(form.completionDate).format(COMMON_REQUEST_DATE_FORMAT),
      })),
      appraisedValue: form.appraisedValue || null,
      occupancy: form.occupancy?.value,
      isRelocationLoan: form.isRelocationLoan ?? null,
      propertyRightsEstateHeld: form.propertyRightsEstateHeld?.value,
      propertyType: form.propertyType?.value,
      numberOfUnits: form.numberOfUnits?.value,
      attachmentType: form.attachmentType?.value,
      purchasePrice: form.propertyValue,
      originalCost: form.originalCost || null,
      status: form.status?.value,
      totalSalesConcessionAmount:
        form.totalSalesConcessionAmount === '' ? null : form.totalSalesConcessionAmount,
      ...getConditionalObject(isManufacturedHome, {
        manufacturedHomeType: form.manufacturedHomeType?.value,
      }),
      ...getConditionalObject(form.leaseholdExpiration, () => ({
        leaseholdExpiration: dayjs(form.leaseholdExpiration).format(COMMON_REQUEST_DATE_FORMAT),
      })),
      hasAffordableLegalRestrictions: form.hasAffordableLegalRestrictions ?? null,
      ...getConditionalObject(form.hasAffordableLegalRestrictions, {
        isConveyable: form.isConveyable ?? null,
      }),
      affordableHomeProviderEin: form.affordableHomeProviderEin || null,
      communityLandTrustEin: form.communityLandTrustEin || null,
      reapiPropertyId: form.reapiPropertyId || null,
      ...getNonQmPropertyRequestData({ form, applicationData, isCondo }),
    },
  };
};

const getAgentAndSellerRequestData = ({ entitySideData }) => {
  if (!entitySideData.sellingAgents) {
    return null;
  }

  const underwritingAgents = [
    ...entitySideData.sellingAgents,
    ...entitySideData.listingAgents,
  ].reduce((acc, { id, isAdded, isDeleted, ...data }) => {
    if (isDeleted) {
      return acc;
    }

    acc.push({ ...data, loanUnderwritingAgentId: isAdded ? null : id });

    return acc;
  }, []);
  const underwritingSellers = entitySideData.sellers.reduce(
    (acc, { id, isAdded, sellerName, fullAddress, type, isDeleted, ...data }) => {
      if (isDeleted) {
        return acc;
      }

      acc.push({
        ...data,
        loanUnderwritingSellerId: isAdded ? null : id,
      });

      return acc;
    },
    [],
  );

  return {
    underwritingAgentsAndSellersData: {
      forSaleByOwner: entitySideData.forSaleByOwner,
      underwritingAgents,
      underwritingSellers,
    },
  };
};

const normalizeProjectDataToSend = form => {
  const { project } = form;

  if (!project) {
    return null;
  }

  const { projectInfo, ...restPropertySections } = project;

  return {
    projectInfo: {
      ...projectInfo,
      projectStatusType: projectInfo.projectStatusType?.value,
      projectClassification: projectInfo.projectClassification?.value,
    },
    ...restPropertySections,
  };
};

const normalizeVestingDataToSend = form => {
  const { vestingTypeId } = form;

  if (!vestingTypeId) {
    return {};
  }

  return {
    vestingInfo: {
      mannerHeld: form.mannerHeld.value,
      bplCompanyName: form.bplCompanyName,
      vestingDescription: form.vestingDescription,
      vestingTypeId: form.vestingTypeId.value,
    },
    ...getConditionalObject(form.isTrustInfoExist, {
      trustInfo: {
        type: form.trustType?.value,
        name: form.trustName,
        establishedDate: form.establishedDate,
        establishedState: form.establishedState?.value,
        trustee1: form.trustee1,
        trustee2: form.trustee2,
        trustee3: form.trustee3,
        trustee4: form.trustee4,
        trustee1BorrowerId: form.trustee1BorrowerId,
        trustee2BorrowerId: form.trustee2BorrowerId,
        trustee3BorrowerId: form.trustee3BorrowerId,
        trustee4BorrowerId: form.trustee4BorrowerId,
        settlor1: form.settlor1,
        settlor2: form.settlor2,
        settlor3: form.settlor3,
        settlor4: form.settlor4,
        settlor1BorrowerId: form.settlor1BorrowerId,
        settlor2BorrowerId: form.settlor2BorrowerId,
        settlor3BorrowerId: form.settlor3BorrowerId,
        settlor4BorrowerId: form.settlor4BorrowerId,
        beneficiary1: form.beneficiary1,
        beneficiary2: form.beneficiary2,
        beneficiary3: form.beneficiary3,
        beneficiary4: form.beneficiary4,
      },
    }),
  };
};

// The save maps the appraisal's neighbourhood after the property, onto the same column, so an
// appraisal rebuilt from the loaded data must carry the location chosen in Property & Occupancy.
const withPropertyLocationType = (appraisal, form) => {
  const locationTypePath = propertyConfigFields.locationType.nameInModel;

  if (!appraisal || !_.has(form, locationTypePath)) {
    return appraisal;
  }

  return _.set(
    _.cloneDeep(appraisal),
    'neighbourhoodAndSite.locationType',
    getFormOptionValue(_.get(form, locationTypePath)),
  );
};

export const getPropertyAndLoanRequestBody = ({
  form,
  initialForm,
  userEditedFieldNames,
  entitySideData,
  initialEntitySideData,
  applicationData,
  loanType,
}) => {
  const appraisal = withPropertyLocationType(
    normalizeAppraisalDataToSend(form, applicationData, loanType),
    form,
  );
  const project = normalizeProjectDataToSend(form);
  const propertyExpenses = getPropertyExpensesRequestData({
    form,
    initialForm,
    entitySideData,
    initialEntitySideData,
    userEditedFieldNames,
  });
  const vesting = normalizeVestingDataToSend(form);
  const transactionDetails = normalizeTransactionDetailsDataToSend(form);

  return {
    ...getConditionalObject(form.street, () => getPropertyRequestData({ form, applicationData })),
    ...getConditionalObject(entitySideData.sellingAgents, () =>
      getAgentAndSellerRequestData({ entitySideData }),
    ),
    ...getConditionalObject(
      checkIsOrderContactsChanged({ entitySideData, initialEntitySideData }),
      () => ({
        [APPRAISAL_ORDER_CONTACTS_KEY]: getAppraisalOrderContactsRequestData({
          entitySideData,
          applicationData,
        }),
      }),
    ),
    ...getConditionalObject(appraisal, {
      appraisal,
    }),
    ...getConditionalObject(form.proposedPropertyExpenses, propertyExpenses),
    ...getConditionalObject(project, {
      project,
    }),
    ...getConditionalObject(transactionDetails, {
      transactionDetails,
    }),
    ...vesting,
    requestMetadata: {
      tabId: LoanApplicationTabId.PROPERTY,
    },
  };
};

// The backend assigns every P&L value verbatim (null clears it), so the block is only sent once the
// P&L section has registered its fields in the form -- a save from any other section must omit it.
const getProfitAndLossTabRequestData = ({ form }) => {
  if (!_.has(form, 'profitAndLoss')) {
    return;
  }

  return replaceEmptyWithNull({
    srpPaidOut: form.profitAndLoss?.srpPaidOut,
    hedgeCost: form.profitAndLoss?.hedgeCost,
    netSellPrice: form.profitAndLoss?.netSellPrice,
    srpFromInvestor: form.profitAndLoss?.srpFromInvestor,
  });
};

// An untouched investor select reads undefined until the investor list has loaded -- that must not clear the
// stored investor. A failed sell side read sends nothing, so blank fields cannot wipe the stored values.
const getSellSideLockTabRequestData = ({ form, applicationData }) => {
  if (!_.has(form, 'sellSideLock') || _.isNil(applicationData.sellSideLock)) {
    return;
  }

  const {
    investorId,
    investorLoanNumber,
    commitmentNumber,
    investorLockDate,
    investorLockExpiration,
  } = form.sellSideLock ?? {};

  return replaceEmptyWithNull({
    investorId: _.isUndefined(investorId)
      ? applicationData.sellSideLock?.investorId
      : investorId?.value,
    investorLoanNumber: _.trim(investorLoanNumber),
    commitmentNumber: _.trim(commitmentNumber),
    investorLockDate: getCommonDateBody(investorLockDate),
    investorLockExpiration: getCommonDateBody(investorLockExpiration),
  });
};

export const getProgramsAndPricingTabRequestData = ({ entitySideData }) => {
  const { selectedProgram, programsAndPricingContext } = entitySideData;

  if (!selectedProgram || !programsAndPricingContext) {
    return {};
  }

  const { loanOptionId, ...loanOption } = selectedProgram;
  const { rateSheetId, lockedRateSheetId, requestId } = programsAndPricingContext;

  return {
    pricing: { rateSheetId: rateSheetId ?? lockedRateSheetId },
    loanOptionSelector: loanOptionId
      ? { loanOptionId, type: LoanOptionSelectorType.BY_LOAN_OPTION_ID }
      : { ...loanOption, requestId, type: LoanOptionSelectorType.BY_PRICING },
  };
};

const normalizeForCompare = value => {
  if (_.isNil(value) || value === '') {
    return null;
  }

  if (_.isPlainObject(value)) {
    if ('value' in value) {
      return normalizeForCompare(value.value);
    }

    if ('amount' in value) {
      return normalizeForCompare(value.amount);
    }
  }

  if (_.isString(value)) {
    const parsed = Number(value);

    return _.isNaN(parsed) ? value : parsed;
  }

  return value;
};

export const hasLoanProgramChanges = (form, applicationData) =>
  loanProgramFieldNames.some(
    key => !_.isEqual(normalizeForCompare(form[key]), normalizeForCompare(applicationData[key])),
  );

const toNullableNumber = value => {
  if (_.isNil(value) || value === '') {
    return null;
  }

  const parsed = Number(value);

  return _.isNaN(parsed) ? null : parsed;
};

const buildIndexPayload = form => {
  const indexTypeId = form[LoanProgramFieldName.INDEX_TYPE_ID]?.value ?? null;
  const margin = toNullableNumber(form[LoanProgramFieldName.INDEX_MARGIN]);
  const rate = toNullableNumber(form[LoanProgramFieldName.INDEX_RATE]);
  const periodDays = toNullableNumber(form[LoanProgramFieldName.INDEX_PERIOD_DAYS]);

  if (_.every([indexTypeId, margin, rate, periodDays], _.isNil)) {
    return {};
  }

  return { index: { indexTypeId, margin, rate, periodDays } };
};

export const buildLoanProgramOverridePayload = (form, applicationData = {}) => {
  const isArm = !!form[LoanProgramFieldName.CHAR_ARM];
  const isHasPrepayPenalty = !!form[LoanProgramFieldName.CHAR_HAS_PREPAY_PENALTY];
  const isHeloc = form[LoanProgramFieldName.PROGRAM_LOAN_TYPE]?.value === LoanType.HELOC;
  const showIndex = isArm || isHeloc;
  const isVariableInitDraw =
    form[LoanProgramFieldName.INIT_DRAW_VAR_RATE_TYPE]?.value === RateType.VARIABLE;
  const isVariableRepay = form[LoanProgramFieldName.REPAY_TYPE]?.value === RateType.VARIABLE;

  return replaceEmptyWithNull(
    {
      ...getConditionalObject(form[LoanProgramFieldName.PROGRAM_LOAN_TYPE]?.value, {
        programLoanType: form[LoanProgramFieldName.PROGRAM_LOAN_TYPE]?.value,
      }),
      loanTerm: Number(form[LoanProgramFieldName.LOAN_TERM]),
      amortTerm: Number(form[LoanProgramFieldName.AMORT_TERM]),
      ...getConditionalObject(form[LoanProgramFieldName.AMORTIZATION_TYPE]?.value, {
        amortizationType: form[LoanProgramFieldName.AMORTIZATION_TYPE]?.value,
      }),
      interestOnlyPeriod: Number(form[LoanProgramFieldName.INTEREST_ONLY_PERIOD]),
      balloonPaymentTermMonths: Number(form[LoanProgramFieldName.BALLOON_TERM_MONTHS]),
      ...getConditionalObject(isHeloc, () => ({
        annualFee: Number(form[LoanProgramFieldName.ANNUAL_FEE]),
        monthlyFee: Number(form[LoanProgramFieldName.MONTHLY_FEE]),
      })),
      ...getConditionalObject(isArm, () => ({
        armTerms: {
          initialCap: Number(form[LoanProgramFieldName.ARM_INITIAL_CAP]),
          adjustmentCap: Number(form[LoanProgramFieldName.ARM_ADJUSTMENT_CAP]),
          subsequentCap: Number(form[LoanProgramFieldName.ARM_SUBSEQUENT_CAP]),
          rateAdjustmentPeriod: Number(form[LoanProgramFieldName.ARM_RATE_ADJ_PERIOD]),
          subsequentRateAdjustmentPeriod: Number(
            form[LoanProgramFieldName.ARM_SUB_RATE_ADJ_PERIOD],
          ),
          floorMinimumRate: Number(form[LoanProgramFieldName.ARM_FLOOR_RATE]),
          ceilingRate: Number(form[LoanProgramFieldName.ARM_CEILING_RATE]),
          adjustmentChangeIndexRate: Number(form[LoanProgramFieldName.ARM_ADJ_CHANGE_INDEX_RATE]),
        },
      })),
      ...getConditionalObject(showIndex, () => buildIndexPayload(form)),
      ...getConditionalObject(isHasPrepayPenalty, () => ({
        prepaymentPenalty: {
          ...getConditionalObject(form[LoanProgramFieldName.PREPAY_FEATURE_TYPE]?.value, {
            featureType: form[LoanProgramFieldName.PREPAY_FEATURE_TYPE]?.value,
          }),
          monthsInAffect: Number(form[LoanProgramFieldName.PREPAY_MONTHS_IN_AFFECT]),
          penaltyChargeTermMonths: Number(form[LoanProgramFieldName.PREPAY_CHARGE_TERM_MONTHS]),
          penaltyChargeRatePercents: Number(form[LoanProgramFieldName.PREPAY_CHARGE_RATE]),
          maxPenaltyAmount: Number(form[LoanProgramFieldName.PREPAY_MAX_AMOUNT]),
          prepaymentDescription: form[LoanProgramFieldName.PREPAY_DESCRIPTION],
        },
      })),
      ...getConditionalObject(isHeloc, () => ({
        initialDrawPeriod: {
          period: Number(form[LoanProgramFieldName.INIT_DRAW_PERIOD]),
          ...getConditionalObject(form[LoanProgramFieldName.INIT_DRAW_MIN_TYPE]?.value, {
            minimumInitialDrawType: form[LoanProgramFieldName.INIT_DRAW_MIN_TYPE]?.value,
          }),
          minimumInitialDrawPercent: Number(form[LoanProgramFieldName.INIT_DRAW_MIN_PERCENT]),
          minimumInitialDrawAmount: Number(form[LoanProgramFieldName.INIT_DRAW_MIN_AMOUNT]),
          ...getConditionalObject(form[LoanProgramFieldName.INIT_DRAW_AMORT_TYPE]?.value, {
            amortizationType: form[LoanProgramFieldName.INIT_DRAW_AMORT_TYPE]?.value,
          }),
          ...getConditionalObject(form[LoanProgramFieldName.INIT_DRAW_VAR_RATE_TYPE]?.value, {
            type: form[LoanProgramFieldName.INIT_DRAW_VAR_RATE_TYPE]?.value,
          }),
          ...getConditionalObject(isVariableInitDraw, {
            variableRate: applicationData[LoanProgramFieldName.INIT_DRAW_VARIABLE_RATE] ?? null,
          }),
        },
        repaymentPeriod: {
          period: Number(form[LoanProgramFieldName.REPAY_PERIOD]),
          ...getConditionalObject(form[LoanProgramFieldName.REPAY_TYPE]?.value, {
            type: form[LoanProgramFieldName.REPAY_TYPE]?.value,
          }),
          ...getConditionalObject(isVariableRepay, {
            variableRate: applicationData[LoanProgramFieldName.REPAY_VARIABLE_RATE] ?? null,
          }),
        },
      })),
      ...getConditionalObject(!!form[LoanProgramFieldName.CHAR_ASSUMABLE], () => ({
        assumableDetailsGroup: {
          ...getConditionalObject(form[LoanProgramFieldName.ASSUMABLE_DETAILS]?.value, {
            assumableDetails: form[LoanProgramFieldName.ASSUMABLE_DETAILS]?.value,
          }),
        },
      })),
      loanCharacteristics: {
        arm: !!form[LoanProgramFieldName.CHAR_ARM],
        assumable: !!form[LoanProgramFieldName.CHAR_ASSUMABLE],
        conforming: !!form[LoanProgramFieldName.CHAR_CONFORMING],
        counselingRequired: !!form[LoanProgramFieldName.CHAR_COUNSELING_REQUIRED],
        interestOnly: !!form[LoanProgramFieldName.CHAR_INTEREST_ONLY],
        hasPrepaymentPenalty: !!form[LoanProgramFieldName.CHAR_HAS_PREPAY_PENALTY],
        runAus: !!form[LoanProgramFieldName.CHAR_RUN_AUS],
        affordableLending: !!form[LoanProgramFieldName.CHAR_AFFORDABLE_LENDING],
        texasCashOut: !!form[LoanProgramFieldName.CHAR_TEXAS_CASH_OUT],
        demandFeature: !!form[LoanProgramFieldName.CHAR_DEMAND_FEATURE],
        negativeAmortization: !!form[LoanProgramFieldName.CHAR_NEG_AMORT],
        balloonPayment: !!form[LoanProgramFieldName.CHAR_BALLOON_PAYMENT],
        bridgeLoan: !!form[LoanProgramFieldName.CHAR_BRIDGE_LOAN],
        constructionOnly: !!form[LoanProgramFieldName.CHAR_CONSTRUCTION_ONLY],
        moreThan2PaymentsCollected: !!form[LoanProgramFieldName.CHAR_MORE_THAN_2_PAYMENTS],
        interestIncreasedAfterDefault: !!form[LoanProgramFieldName.CHAR_INTEREST_INCREASED],
        demandClause: !!form[LoanProgramFieldName.CHAR_DEMAND_CLAUSE],
        lenderPaidMI: !!form[LoanProgramFieldName.CHAR_LENDER_PAID_MI],
      },
    },
    { includeNested: true },
  );
};

const formatMiDateForRequest = date => dayjs(date).format(COMMON_REQUEST_DATE_FORMAT);

// The backend keeps the stored value on null, and the MI order dates are full timestamps written
// by the order integration -- re-sending an untouched day-precision picker value would truncate them.
const getMiDateRequestValue = (formDate, storedDate) => {
  if (!formDate) {
    return null;
  }

  const requestDate = formatMiDateForRequest(formDate);
  const isUnchanged =
    !!storedDate &&
    requestDate === formatMiDateForRequest(getDateWithoutTimezoneShift(storedDate.toString()));

  return isUnchanged ? null : requestDate;
};

const getStoredSectionForm = ({ applicationData, config }) =>
  getConfigInfo({ applicationData, config, isNestedForm: true }).initialFormValues;

const getLoadedMiForm = applicationData =>
  getStoredSectionForm({
    applicationData,
    config: getMortgageInsuranceConfig({
      miProductType: applicationData.miProductType,
      miCompanyType: applicationData.miCompanyType,
    }),
  });

const getChangedValue = (value, loadedValue) => (_.isEqual(value, loadedValue) ? null : value);

// The backend keeps the stored MI value on null, and the order integration writes the insurer outcome
// (status, decision, certificate, ...) asynchronously while the editor may be open -- so only fields
// the user changed from what was loaded are written, and a stale loaded value never overwrites it.
const getMiDetailsRequestData = ({ form, applicationData }) => {
  const loadedForm = getLoadedMiForm(applicationData);

  const getChangedSelectValue = name =>
    getChangedValue(form[name]?.value ?? null, loadedForm[name]?.value ?? null);
  const getChangedTextValue = name => getChangedValue(form[name] ?? '', loadedForm[name] ?? '');
  const getChangedNumberValue = name =>
    getChangedValue(toNullableNumber(form[name]), toNullableNumber(loadedForm[name]));

  return {
    miProductType: getChangedSelectValue('miProductType'),
    miPaymentType: getChangedSelectValue('miPaymentType'),
    miRefundable: !!form.miRefundable,
    miRenewalType: getChangedSelectValue('miRenewalType'),
    miFinanced: !!form.miFinanced,
    miUpfrontPremiumPercent: getChangedNumberValue('miUpfrontPremiumPercent'),
    miCompanyType: getChangedSelectValue('miCompanyType'),
    miCompanyOtherDescription: getChangedTextValue('miCompanyOtherDescription'),
    miApplicationType: getChangedSelectValue('miApplicationType'),
    miOrderStatus: getChangedSelectValue('miOrderStatus'),
    miDecisionType: getChangedSelectValue('miDecisionType'),
    miOrderedDate: getMiDateRequestValue(form.miOrderedDate, applicationData?.miOrderedDate),
    miDecisionDate: getMiDateRequestValue(form.miDecisionDate, applicationData?.miDecisionDate),
    miActivatedDate: getMiDateRequestValue(form.miActivatedDate, applicationData?.miActivatedDate),
    miCertificateIdentifier: getChangedTextValue('miCertificateIdentifier'),
    miCertificateExpirationDate: getMiDateRequestValue(
      form.miCertificateExpirationDate,
      applicationData?.miCertificateExpirationDate,
    ),
    miQuoteIdentifier: getChangedTextValue('miQuoteIdentifier'),
    miQuoteExpirationDate: getMiDateRequestValue(
      form.miQuoteExpirationDate,
      applicationData?.miQuoteExpirationDate,
    ),
    miInitialPremiumRatePercent: getChangedNumberValue('miInitialPremiumRatePercent'),
    miInitialPremiumTermMonths: getChangedNumberValue('miInitialPremiumTermMonths'),
    miRenewalPremiumRatePercent: getChangedNumberValue('miRenewalPremiumRatePercent'),
    miRenewalPremiumTermMonths: getChangedNumberValue('miRenewalPremiumTermMonths'),
    miUpfrontPremiumAmount: getChangedNumberValue('miUpfrontPremiumAmount'),
    miPremiumTaxAmount: getChangedNumberValue('miPremiumTaxAmount'),
  };
};

const toStoredValueOptions = storedValue =>
  _.castArray(storedValue ?? [])
    .filter(value => !_.isNil(value))
    .map(value => ({ value }));

const hasMiChanges = ({ form, applicationData }) => {
  const { miRefundable, miFinanced, ...changedValues } = getMiDetailsRequestData({
    form,
    applicationData,
  });

  return (
    miRefundable !== !!applicationData.miRefundable ||
    miFinanced !== !!applicationData.miFinanced ||
    _.some(changedValues, value => !_.isNil(value))
  );
};

// The loan-info PUT overwrites every loan-info field it carries (loan amount, purpose, lead source,
// escrow, agency case number, ...). When only the Mortgage Insurance section is loaded, the Loan Info
// fields are rebuilt from the stored loan through the same configs the Loan Info section registers,
// so an MI-only save re-sends the stored values instead of blanks. Dynamic option lists are seeded
// from the stored values themselves because they are only fetched once the Loan Info section mounts.
const getStoredLoanInfoForm = ({ applicationData, isLoanNoPpeEngineType }) => {
  const {
    loanPurpose,
    mortgageType,
    refinancePurpose,
    escrowSettings,
    loanWorkflow,
    nationalHousingActSection,
    housingFinanceAgencyId,
    housingFinanceAgencyOtherDescription,
  } = applicationData;

  const insuranceEscrowIds = (
    escrowSettings?.insuranceEscrowSettings?.insuranceImpoundsEscrowed ?? []
  ).map(({ applicationInsuranceId }) => applicationInsuranceId);
  const isOtherHousingFinanceAgency = !!housingFinanceAgencyOtherDescription;

  const config = {
    groups: [
      ...getLoanDetailsConfig({
        loanPurpose,
        mortgageType,
        insuranceEscrowIds,
        isRefinanceCashOutSubflow: checkIsRefinanceCashOutSubflow({ refinancePurpose }),
        mortgageLienPriorityOptions: toStoredValueOptions(applicationData.lienPriority),
        buyDownOptions: toStoredValueOptions(applicationData.buyDown),
      }).groups,
      ...getAdditionalLoanInformationConfig({
        loanPurpose,
        mortgageType,
        loanWorkflow,
        nationalHousingActSection,
        isLoanNoPpeEngineType,
        isOtherHousingFinanceAgency,
        housingFinanceAgenciesOptions: toStoredValueOptions(housingFinanceAgencyId).map(option => ({
          ...option,
          isOther: isOtherHousingFinanceAgency,
        })),
        investorFeatureCodesOptions: toStoredValueOptions(applicationData.investorCodes),
        leadSourcesOptions: toStoredValueOptions(applicationData.leadSource),
        programIdentifiersOptions: toStoredValueOptions(applicationData.loanProgramIdentifiers),
      }).groups,
      ...getDocumentVerificationTypesConfig().groups,
      ...getNonQmLoanInfoConfig({ isPrepaymentPenaltyVisible: true }).groups,
    ],
  };

  return getStoredSectionForm({ applicationData, config });
};

// A form that places only some Loan Info fields (e.g. a loan editor custom form) seeds the rest of
// the section from option lists that may not contain the stored value (an Optimal Blue loan only
// lists lead sources mapped to its channel), so its untouched fields cannot be trusted to round-trip.
const getEditedLoanInfoRequestForm = ({
  form,
  editedLoanInfoFieldPaths,
  isLoanNoPpeEngineType,
  applicationData,
}) => {
  const editedForm = [...editedLoanInfoFieldPaths].reduce(
    (acc, path) => _.set(acc, path, _.cloneDeep(_.get(form, path))),
    getStoredLoanInfoForm({ applicationData, isLoanNoPpeEngineType }),
  );

  return 'miProductType' in form
    ? { ...editedForm, ..._.pick(form, mortgageInsuranceSectionFieldsKeys) }
    : editedForm;
};

const getLoanInfoRequestForm = ({
  form,
  isLoanNoPpeEngineType,
  applicationData,
  editedLoanInfoFieldPaths,
}) => {
  if (editedLoanInfoFieldPaths) {
    return getEditedLoanInfoRequestForm({
      form,
      editedLoanInfoFieldPaths,
      isLoanNoPpeEngineType,
      applicationData,
    });
  }

  if (form.baseLoanAmount) {
    return form;
  }

  const isMiSectionLoaded = 'miProductType' in form;

  if (!isMiSectionLoaded || !hasMiChanges({ form, applicationData })) {
    return;
  }

  return {
    ...getStoredLoanInfoForm({ applicationData, isLoanNoPpeEngineType }),
    ...form,
  };
};

const getTangibleNetBenefitRequestData = ({ form, applicationData }) => {
  const reason = form.tangibleNetBenefitReason?.value ?? applicationData?.tangibleNetBenefitReason;

  const isLowerInterestRate = reason === TangibleNetBenefitReasonType.LOWER_INTEREST_RATE;
  const isAmortizationPeriod =
    reason === TangibleNetBenefitReasonType.BENEFICIAL_CHANGE_IN_AMORTIZATION_PERIOD;
  const isOther = reason === TangibleNetBenefitReasonType.OTHER;

  const rawRate =
    form.tangibleNetBenefitCurrentInterestRate ??
    applicationData?.tangibleNetBenefitCurrentInterestRate;
  const rawMonths =
    form.tangibleNetBenefitCurrentAmortizationPeriodMonths ??
    applicationData?.tangibleNetBenefitCurrentAmortizationPeriodMonths;
  const description =
    form.tangibleNetBenefitDescription ?? applicationData?.tangibleNetBenefitDescription ?? null;

  return {
    reason,
    ...getConditionalObject(isLowerInterestRate && rawRate, () => ({
      currentInterestRate: Number(rawRate),
    })),
    ...getConditionalObject(isAmortizationPeriod && rawMonths, () => ({
      currentAmortizationPeriodMonths: parseInt(rawMonths, 10),
    })),
    ...getConditionalObject(isOther, { description }),
  };
};

// The group saves all four fields (a blank DSCR clears the stored ratio so Pricing computes it); a
// hidden prepayment choice is re-sent as stored so it is never cleared by being out of view. A save
// rebuilt from stored values (MI-only, custom form) re-sends the stored four.
const getNonQmLoanInfoRequestData = ({ form, applicationData }) => {
  const isPrepaymentPenaltyShown = checkIsPrepaymentPenaltyChoiceVisible({ form, applicationData });

  return {
    incomeDocumentationType: getFormOptionValue(form.incomeDocumentationType),
    debtServiceCoverageRatio: toNullableNumber(form.debtServiceCoverageRatio),
    prepaymentPenaltyTermMonths: isPrepaymentPenaltyShown
      ? toNullableNumber(form.prepaymentPenaltyTermMonths)
      : (applicationData.prepaymentPenaltyTermMonths ?? null),
    prepaymentPenaltyStructure: isPrepaymentPenaltyShown
      ? getFormOptionValue(form.prepaymentPenaltyStructure)
      : (applicationData.prepaymentPenaltyStructure ?? null),
  };
};

const getLoanInfoRequestFlags = ({ form, sectionForm, editedLoanInfoFieldPaths }) => {
  const isLoanInfoSectionLoaded = form === sectionForm;

  return {
    isLoanInfoSectionLoaded,
    isCashOutAmountManual:
      isLoanInfoSectionLoaded || !!editedLoanInfoFieldPaths?.has('cashOutAmount'),
  };
};

const buildLoanInfoTabRequestData = ({
  form,
  isLoanNoPpeEngineType,
  applicationData,
  isLoanInfoSectionLoaded,
  isCashOutAmountManual,
}) => {
  const { escrowSettings } = applicationData;
  const { insuranceEscrowSettings } = escrowSettings || {};
  const { floodInsuranceImpoundId } = insuranceEscrowSettings || {};

  const isRefinance = checkIsRefinanceFlow(form.loanPurpose?.value);
  const isAdditionalLoan = checkIsAdditionalLoanFlow(form.loanPurpose?.value);
  const isRefinanceCashOutSubflow = checkIsRefinanceCashOutSubflow({
    refinancePurpose: form.refinancePurpose?.value,
  });

  return {
    details: {
      baseLoanAmount: form.baseLoanAmount,
      // downPaymentValue ($) is persisted via the loan-info save endpoint. downPaymentPercent (%)
      // is a UI-only calculator derived from downPaymentValue (see LoanInfo.js's sync effect),
      // fully decoupled from baseLoanAmount -- it is never sent.
      downPaymentValue: form.downPaymentValue ?? null,
      loanPurpose: form.loanPurpose?.value,
      escrowSettings: {
        isEscrowed: form.escrowSettings?.isEscrowed,
        taxesEscrowSettings: {
          isEscrowed: form.escrowSettings?.taxesEscrowSettings?.isEscrowed,
        },
        insuranceEscrowSettings: {
          insuranceImpoundsEscrowed: _.reduce(
            form.escrowSettings?.insuranceEscrowSettings ?? {},
            (acc, curr, key) => {
              if (key !== floodInsuranceImpoundId) {
                acc[key] = curr.isEscrowed;
              }

              return acc;
            },
            {},
          ),
        },
      },
      ...getConditionalObject(form.loanPurpose?.value, () => ({
        loanPurposeType: {
          type: form.loanPurpose?.value,
          ...getConditionalObject(isRefinance, () => ({
            refinancePurpose: form.refinancePurpose?.value,
            ...getConditionalObject(isRefinanceCashOutSubflow, {
              cashOutAmount: form.cashOutAmount || null,
              // The backend assigns this flag verbatim and pricing pins a manual cash-out, so only a
              // save that showed the cash-out field may mark it manual; a rebuilt MI-only save keeps
              // the stored flag so a computed cash-out stays computed.
              isManualCashoutAmount: isCashOutAmountManual
                ? true
                : (applicationData.isManualCashoutAmount ?? null),
            }),
            ...getConditionalObject(
              form.tangibleNetBenefitReason?.value ?? applicationData?.tangibleNetBenefitReason,
              () => ({
                tangibleNetBenefit: getTangibleNetBenefitRequestData({ form, applicationData }),
              }),
            ),
          })),
          ...getConditionalObject(isAdditionalLoan, {
            refinancePurpose: form.refinancePurpose?.value,
            helocPurpose: form.helocPurpose?.value,
            additionalLoanSecondMortgageType: form.additionalLoanSecondMortgageType?.value,
            additionalLoanDrawAmount: form.additionalLoanDrawAmount,
          }),
        },
      })),
      lienPriority: form.lienPriority?.value,
      buyDownId: form.buyDown?.value ?? null,
      vaTotalEntitlement: form.vaTotalEntitlement || null,
      ...getConditionalObject('miProductType' in form, () =>
        getMiDetailsRequestData({ form, applicationData }),
      ),
    },
    additionalInfo: {
      investorCodes: form.investorCodes?.map(({ value }) => value),
      programIdentifiers: form.loanProgramIdentifiers?.map(({ value }) => value),
      ...getConditionalObject(!isLoanNoPpeEngineType, {
        leadSourceId: form.leadSource?.value,
      }),
      nationalHousingActSection: form.nationalHousingActSection?.value,
      fha203kConsultantId: form.fha203kConsultantId || null,
      fhaBorrowerCertification: form.fhaBorrowerCertification ?? null,
      type: isLoanNoPpeEngineType ? LoanEngineType.NO_PPE : LoanEngineType.GENERAL,
      agencyCaseNumber: form.agencyCaseNumber || null,
      agencyCaseNumberIssueDate: form.agencyCaseNumberIssueDate
        ? dayjs(form.agencyCaseNumberIssueDate).format(COMMON_REQUEST_DATE_FORMAT)
        : null,
      governmentRefinancePurpose: form.governmentRefinancePurpose?.value,
      hfaInfo: form.housingFinanceAgencyId
        ? {
            id: form.housingFinanceAgencyId.value,
            ...getConditionalObject(form.housingFinanceAgencyId.isOther, {
              otherDescription: form.housingFinanceAgencyOtherDescription,
            }),
          }
        : null,
    },
    documentVerificationTypes: replaceEmptyWithNull({
      asset: form.documentVerificationAssetType?.value,
      employment: form.documentVerificationEmploymentType?.value,
      income: form.documentVerificationIncomeType?.value,
    }),
    ...getConditionalObject(
      isLoanInfoSectionLoaded && hasLoanProgramChanges(form, applicationData),
      () => ({
        loanProgramOverride: buildLoanProgramOverridePayload(form, applicationData),
      }),
    ),
    nonQm: getNonQmLoanInfoRequestData({ form, applicationData }),
  };
};

export const getLoanInfoTabRequestData = ({
  form: sectionForm,
  isLoanNoPpeEngineType,
  applicationData,
  editedLoanInfoFieldPaths,
}) => {
  const form = getLoanInfoRequestForm({
    form: sectionForm,
    isLoanNoPpeEngineType,
    applicationData,
    editedLoanInfoFieldPaths,
  });

  if (!form) {
    return;
  }

  const { isLoanInfoSectionLoaded, isCashOutAmountManual } = getLoanInfoRequestFlags({
    form,
    sectionForm,
    editedLoanInfoFieldPaths,
  });

  return buildLoanInfoTabRequestData({
    form,
    isLoanNoPpeEngineType,
    applicationData,
    isLoanInfoSectionLoaded,
    isCashOutAmountManual,
  });
};

// The baseline is what the same save would have sent had nothing been edited: the form as the modal opened
// when the Loan Info section is loaded, otherwise the stored loan (plus the loaded MI section when the save
// carries Mortgage Insurance), mirroring how getLoanInfoRequestForm rebuilds a partial save.
const getLoanInfoBaselineForm = ({
  form,
  initialForm,
  isLoanInfoSectionLoaded,
  isLoanNoPpeEngineType,
  applicationData,
}) => {
  if (isLoanInfoSectionLoaded) {
    return initialForm;
  }

  return {
    ...getStoredLoanInfoForm({ applicationData, isLoanNoPpeEngineType }),
    ...getConditionalObject('miProductType' in form, () => getLoadedMiForm(applicationData)),
  };
};

// A changed key the Loan Info form derives (Loan Amount off a down payment edit, the down payment off a Loan Amount
// edit, a cash-out cleared for recalculation) was recalculated when the user touched none of its own fields, so the
// backend records it as such instead of crediting it to the user. Without the user's edits every change is theirs.
const getIsRecalculatedLoanDetailsKey = userEditedFieldNames => key => {
  const sourceFieldNames = LOAN_DETAILS_RECALCULATED_KEY_SOURCES[key];

  return (
    !!userEditedFieldNames &&
    !!sourceFieldNames &&
    !sourceFieldNames.some(name => userEditedFieldNames.has(name))
  );
};

// The loan-info save sends only what the user changed, as Option patches. On the backend every author's
// pending changes are the diff baseline, so re-sending an untouched field (even at the value this user saw)
// would be recorded as their change and revert another author's pending value.
const getLoanInfoPatchRequestData = ({
  form: sectionForm,
  initialForm,
  userEditedFieldNames,
  isLoanNoPpeEngineType,
  applicationData,
  editedLoanInfoFieldPaths,
}) => {
  const form = getLoanInfoRequestForm({
    form: sectionForm,
    isLoanNoPpeEngineType,
    applicationData,
    editedLoanInfoFieldPaths,
  });

  if (!form) {
    return;
  }

  const { isLoanInfoSectionLoaded, isCashOutAmountManual } = getLoanInfoRequestFlags({
    form,
    sectionForm,
    editedLoanInfoFieldPaths,
  });

  if (isLoanInfoSectionLoaded && !initialForm) {
    return getLoanInfoTabRequestData({
      form: sectionForm,
      isLoanNoPpeEngineType,
      applicationData,
      editedLoanInfoFieldPaths,
    });
  }

  const baselineForm = getLoanInfoBaselineForm({
    form,
    initialForm,
    isLoanInfoSectionLoaded,
    isLoanNoPpeEngineType,
    applicationData,
  });

  const current = buildLoanInfoTabRequestData({
    form,
    isLoanNoPpeEngineType,
    applicationData,
    isLoanInfoSectionLoaded,
    isCashOutAmountManual,
  });
  const baseline = buildLoanInfoTabRequestData({
    form: baselineForm,
    isLoanNoPpeEngineType,
    applicationData,
    isLoanInfoSectionLoaded,
    isCashOutAmountManual,
  });
  // The override is built only when the program differs from the stored loan, so its baseline is built from the
  // opening form directly; only the override fields the user changed are sent.
  const loanProgramOverridePatch = current.loanProgramOverride
    ? getPatch({
        current: current.loanProgramOverride,
        baseline: buildLoanProgramOverridePayload(baselineForm, applicationData),
        keys: _.keys(current.loanProgramOverride),
      })
    : {};

  return {
    detailsPatch: getPatch({
      current: current.details,
      baseline: baseline.details,
      keys: LOAN_DETAILS_PATCH_KEYS,
      isRecalculatedKey: getIsRecalculatedLoanDetailsKey(
        isLoanInfoSectionLoaded ? userEditedFieldNames : null,
      ),
    }),
    additionalInfoPatch: getPatch({
      current: current.additionalInfo,
      baseline: baseline.additionalInfo,
      keys: LOAN_ADDITIONAL_INFO_PATCH_KEYS,
    }),
    documentVerificationTypesPatch: getPatch({
      current: current.documentVerificationTypes,
      baseline: baseline.documentVerificationTypes,
      keys: LOAN_DOCUMENT_VERIFICATION_TYPES_PATCH_KEYS,
    }),
    nonQmPatch: getPatch({
      current: current.nonQm,
      baseline: baseline.nonQm,
      keys: LOAN_NON_QM_PATCH_KEYS,
    }),
    ...getConditionalObject(!_.isEmpty(loanProgramOverridePatch), () => ({
      loanProgramOverridePatch,
    })),
  };
};

export const getLoanInfoForLoanOptions = ({ form, isLoanNoPpeEngineType, applicationData }) => {
  const { escrowSettings } = applicationData || {};
  const { insuranceEscrowSettings } = escrowSettings || {};
  const { floodInsuranceImpoundId, insuranceImpoundsEscrowed } = insuranceEscrowSettings || {};

  const loanPurpose = form.loanPurpose?.value ?? applicationData?.loanPurpose;
  const isRefinance = checkIsRefinanceFlow(loanPurpose);
  const isAdditionalLoan = checkIsAdditionalLoanFlow(loanPurpose);

  const refinancePurpose = form.refinancePurpose?.value ?? applicationData?.refinancePurpose;
  const isRefinanceCashOutSubflow = checkIsRefinanceCashOutSubflow({
    refinancePurpose,
  });

  const baseLoanAmount = form.baseLoanAmount ?? applicationData?.baseLoanAmount;
  const hasFormEscrowSettings = !!form.escrowSettings;
  const helocPurpose = form.helocPurpose?.value ?? applicationData?.helocPurpose;
  const additionalLoanSecondMortgageType =
    form.additionalLoanSecondMortgageType?.value ??
    applicationData?.additionalLoanSecondMortgageType;
  const additionalLoanDrawAmount =
    form.additionalLoanDrawAmount ?? applicationData?.additionalLoanDrawAmount;
  const cashOutAmount = form.cashOutAmount ?? applicationData?.cashOutAmount;
  const lienPriority = form.lienPriority?.value ?? applicationData?.lienPriority;
  const buyDownId = form.buyDown?.value ?? applicationData?.buyDown;
  const vaTotalEntitlement = form.vaTotalEntitlement ?? applicationData?.vaTotalEntitlement;
  const miProductType = form.miProductType?.value ?? applicationData?.miProductType;
  const miPaymentType = form.miPaymentType?.value ?? applicationData?.miPaymentType;
  const miRefundable = form.miRefundable ?? applicationData?.miRefundable;
  const miRenewalType = form.miRenewalType?.value ?? applicationData?.miRenewalType;
  const miFinanced = form.miFinanced ?? applicationData?.miFinanced;
  const miUpfrontPremiumPercent =
    form.miUpfrontPremiumPercent ?? applicationData?.miUpfrontPremiumPercent;
  const investorCodes =
    form.investorCodes?.map(({ value }) => value) ??
    applicationData?.investorCodes?.map(({ id }) => id) ??
    [];
  const programIdentifiers =
    form.loanProgramIdentifiers?.map(({ value }) => value) ??
    applicationData?.loanProgramIdentifiers ??
    [];
  const leadSourceId = form.leadSource?.value ?? applicationData?.leadSource;
  const nationalHousingActSection =
    form.nationalHousingActSection?.value ?? applicationData?.nationalHousingActSection;
  const agencyCaseNumber = form.agencyCaseNumber ?? applicationData?.agencyCaseNumber;
  const governmentRefinancePurpose =
    form.governmentRefinancePurpose?.value ?? applicationData?.governmentRefinancePurpose;
  const housingFinanceAgencyId =
    form.housingFinanceAgencyId?.value ?? applicationData?.housingFinanceAgencyId;
  const housingFinanceAgencyIsOther =
    form.housingFinanceAgencyId?.isOther ?? applicationData?.housingFinanceAgencyId;
  const housingFinanceAgencyOtherDescription =
    form.housingFinanceAgencyOtherDescription ??
    applicationData?.housingFinanceAgencyOtherDescription;
  const documentVerificationAssetType =
    form.documentVerificationAssetType?.value ?? applicationData?.documentVerificationAssetType;
  const documentVerificationEmploymentType =
    form.documentVerificationEmploymentType?.value ??
    applicationData?.documentVerificationEmploymentType;
  const documentVerificationIncomeType =
    form.documentVerificationIncomeType?.value ?? applicationData?.documentVerificationIncomeType;
  const isEscrowed = form.escrowSettings?.isEscrowed ?? escrowSettings?.isEscrowed;
  const taxesIsEscrowed =
    form.escrowSettings?.taxesEscrowSettings?.isEscrowed ??
    escrowSettings?.taxesEscrowSettings?.isEscrowed;

  if (!baseLoanAmount) {
    return;
  }

  const getInsuranceImpoundsEscrowed = () => {
    if (hasFormEscrowSettings) {
      return _.reduce(
        form.escrowSettings?.insuranceEscrowSettings ?? {},
        (acc, curr, key) => {
          if (key !== floodInsuranceImpoundId) {
            acc[key] = curr.isEscrowed;
          }

          return acc;
        },
        {},
      );
    }

    return (insuranceImpoundsEscrowed || []).reduce(
      (acc, { applicationInsuranceId, isEscrowed }) => {
        if (applicationInsuranceId !== floodInsuranceImpoundId) {
          acc[applicationInsuranceId] = isEscrowed;
        }

        return acc;
      },
      {},
    );
  };

  return replaceEmptyWithNull(
    {
      details: {
        baseLoanAmount,
        loanPurpose,
        escrowSettings: {
          isEscrowed,
          taxesEscrowSettings: {
            isEscrowed: taxesIsEscrowed,
          },
          insuranceEscrowSettings: {
            insuranceImpoundsEscrowed: getInsuranceImpoundsEscrowed(),
          },
        },
        ...getConditionalObject(loanPurpose, () => ({
          loanPurposeType: {
            type: loanPurpose,
            ...getConditionalObject(isRefinance, () => ({
              refinancePurpose,
              ...getConditionalObject(isRefinanceCashOutSubflow, {
                cashOutAmount,
              }),
              ...getConditionalObject(
                form.tangibleNetBenefitReason?.value ?? applicationData?.tangibleNetBenefitReason,
                () => ({
                  tangibleNetBenefit: getTangibleNetBenefitRequestData({ form, applicationData }),
                }),
              ),
            })),
            ...getConditionalObject(isAdditionalLoan, {
              refinancePurpose,
              helocPurpose,
              additionalLoanSecondMortgageType,
              additionalLoanDrawAmount,
            }),
          },
        })),
        lienPriority,
        buyDownId: buyDownId ?? null,
        vaTotalEntitlement,
        miProductType,
        miPaymentType,
        miRefundable,
        miRenewalType,
        miFinanced,
        miUpfrontPremiumPercent,
      },
      additionalInfo: {
        investorCodes,
        programIdentifiers,
        ...getConditionalObject(!isLoanNoPpeEngineType, {
          leadSourceId,
        }),
        nationalHousingActSection,
        type: isLoanNoPpeEngineType ? LoanEngineType.NO_PPE : LoanEngineType.GENERAL,
        agencyCaseNumber,
        governmentRefinancePurpose,
        hfaInfo: housingFinanceAgencyId
          ? {
              id: housingFinanceAgencyId,
              ...getConditionalObject(housingFinanceAgencyIsOther, {
                otherDescription: housingFinanceAgencyOtherDescription,
              }),
            }
          : null,
      },
      documentVerificationTypes: {
        asset: documentVerificationAssetType,
        employment: documentVerificationEmploymentType,
        income: documentVerificationIncomeType,
      },
    },
    { includeNested: true },
  );
};

const getCreditsTabRequestData = ({ entitySideData }) => {
  return entitySideData.credits?.reduce(
    (acc, credit) => {
      if (credit.isDeleted) {
        acc.delete.push(credit.id);

        return acc;
      }

      const creditRequestData = {
        description: credit.description,
        purchaseCreditType: credit.purchaseCreditType,
        purchaseSourceType: {
          type: credit.sourceType,
          otherDescription: credit.otherDescription?.length ? credit.otherDescription : null,
        },
        creditType: credit.creditType,
        amount: credit.amount,
      };

      if (credit.isAdded) {
        acc.create.push(creditRequestData);

        return acc;
      }

      acc.update[credit.id] = creditRequestData;

      return acc;
    },
    {
      create: [],
      update: {},
      delete: [],
    },
  );
};

const getOBCustomFieldsTabRequestData = ({ form, customFields }) => {
  if (!_.size(form.obCustomFields)) {
    return undefined;
  }

  return {
    customFields: customFields.items
      ?.map(i => {
        const fieldName = `${i.columnName}${i.customFieldInputName}${i.submissionForm}`;
        const fieldValue = form.obCustomFields[fieldName];

        const resolvedValue = (_.isObject(fieldValue) ? fieldValue.value : fieldValue) || null;

        return {
          name: i.customFieldInputName,
          columnName: i.columnName,
          fieldValue: resolvedValue,
          submissionForm: i.submissionForm,
          fieldType: i.valueType,
          isRequired: i.isRequired,
        };
      })
      .filter(i => !_.isUndefined(i.fieldValue)),
  };
};

const getAdditionalMortgagesTabRequestData = ({ form, applicationData, entitySideData }) => {
  return (
    form.additionalMortgages &&
    Object.entries(form.additionalMortgages).reduce((acc, [fieldKey, mortgage]) => {
      const isNewAdded =
        !!entitySideData.additionalMortgages?.newAddedAdditionalMortgages?.includes(fieldKey);
      const isDeleted = !!(
        applicationData.initiallyDeletedAdditionalMortgagesFieldsKeys?.includes(fieldKey) ||
        entitySideData.additionalMortgages?.deletedAdditionalMortgages?.includes(fieldKey)
      );

      if (isDeleted) {
        return acc;
      }

      const {
        monthsDeferred,
        sourceOfFunds,
        lienPriorityType,
        deferredType,
        isSubordinate,
        excludePayment,
        heloc,
        isAffordableLoan,
        highLimit,
        ...rest
      } = mortgage;

      acc.push({
        ...rest,
        ...getConditionalObject(!isNewAdded, {
          id: applicationData.additionalMortgages[fieldKey]?.id,
        }),
        lienPriorityType: lienPriorityType?.value,
        sourceOfFunds: sourceOfFunds?.value,
        monthsDeferred: Number(monthsDeferred) || 0,
        deferredType: deferredType?.value,
        ...replaceEmptyWithFalse({
          isSubordinate,
          excludePayment,
          heloc,
        }),
        isAffordableLoan: !heloc ? !!isAffordableLoan : null,
        ...getConditionalObject(heloc, {
          highLimit,
        }),
      });

      return acc;
    }, [])
  );
};

const getLoanTagAssignmentsTabRequestData = ({ entitySideData }) => {
  const { loanTags, deletedLoanTags, newAddedLoanTags } = entitySideData;

  return (
    loanTags && {
      delete: deletedLoanTags,
      create: newAddedLoanTags,
      restore: [],
    }
  );
};

const ROLE_ASSIGNMENT_TYPE_MAP = {
  ASSIGN: 'Assign',
  REASSIGN: 'Reassign',
  UNASSIGN: 'Unassign',
};

const getLoanRoleAssignmentsTabRequestData = ({ entitySideData, applicationData }) => {
  const { loanRoles, newAssignedLoanRoles, unassignedLoanRoles, reassignedLoanRoles } =
    entitySideData;

  const loanRolesById = _.keyBy(loanRoles, 'loanRoleId');
  const savedLoanRolesById = _.keyBy(applicationData.loanRoles, 'loanRoleId');

  const pickSpecificLoanRoles = (loanRolesIds, type) =>
    loanRolesIds.map(id => {
      const loanRoleData = loanRolesById[id] ?? savedLoanRolesById[id];

      const { assignedDate: operationTime, userId, loanRoleId } = loanRoleData;

      return {
        loanRoleId,
        type,
        ...getConditionalObject(type !== ROLE_ASSIGNMENT_TYPE_MAP.UNASSIGN, {
          operationTime,
          userId,
        }),
      };
    });

  return (
    loanRoles && {
      delete: pickSpecificLoanRoles(unassignedLoanRoles, ROLE_ASSIGNMENT_TYPE_MAP.UNASSIGN),
      create: pickSpecificLoanRoles(newAssignedLoanRoles, ROLE_ASSIGNMENT_TYPE_MAP.ASSIGN),
      reassign: pickSpecificLoanRoles(reassignedLoanRoles, ROLE_ASSIGNMENT_TYPE_MAP.REASSIGN),
    }
  );
};

const getLockInfoTabRequestData = ({
  entitySideData,
  withExtensionLimitOverride = false,
  isLockExtensionsV2 = false,
  isExternalRequestType = false,
}) => {
  if (!entitySideData.adjustments) {
    return undefined;
  }

  const result = entitySideData.adjustments.reduce((acc, item) => {
    switch (item.type) {
      case CustomAdjustmentType.CUSTOM_ADJUSTMENT: {
        if (!acc.customAdjustments && (item.isAdded || item.isDeleted)) {
          acc.customAdjustments = {
            create: [],
            delete: [],
            ...getConditionalObject(isLockExtensionsV2, {
              deleteV2: [],
            }),
          };
        }

        if (item.isAdded) {
          acc.customAdjustments.create.push({
            customAdjustmentTypeId: item.customAdjustmentTypeId,
            amount: item.amount,
            name: item.name,
            ...getConditionalObject(isExternalRequestType, {
              comment: item.comment,
              isExternal: true,
            }),
          });
        }

        if (item.isDeleted) {
          const deleteArrPath = isLockExtensionsV2 ? 'deleteV2' : 'delete';

          acc.customAdjustments[deleteArrPath].push(item.id);
        }

        break;
      }

      case CustomAdjustmentType.EXTENSION: {
        if (!acc.lockExtensions && (item.isAdded || item.isDeleted)) {
          acc.lockExtensions = {
            ...(isLockExtensionsV2
              ? {
                  createV2: [],
                }
              : {
                  create: [],
                }),
            delete: [],
            withExtensionLimitOverride,
          };
        }

        if (item.isAdded && !isLockExtensionsV2) {
          acc.lockExtensions.create.push({
            ...getConditionalObject(!!item.lockExtensionId, {
              lockExtensionId: item.lockExtensionId,
              lockExtensionType: item.lockExtensionType,
            }),
            ...getConditionalObject(!item.lockExtensionId, {
              lockExtensionType: item.lockExtensionType,
              term: item.term,
              price: item.amount,
              llpaTypeId: item.llpaTypeId,
            }),
          });
        }

        if (item.isAdded && isLockExtensionsV2) {
          acc.lockExtensions.createV2.push({
            type: isExternalRequestType
              ? LockExtensionsRequestType.EXTERNAL
              : LockExtensionsRequestType.LENDER,
            ...getConditionalObject(!!item.lockExtensionId, {
              lockExtensionId: item.lockExtensionId,
              lockExtensionType: item.lockExtensionType,
            }),
            ...getConditionalObject(!item.lockExtensionId, {
              lockExtensionType: item.lockExtensionType,
              term: item.term,
              price: item.amount,
              llpaTypeId: item.llpaTypeId,
            }),
          });
        }

        if (item.isDeleted) {
          acc.lockExtensions.delete.push(item.id);
        }

        break;
      }

      case CustomAdjustmentType.FLOAT_DOWN: {
        if (!acc.floatDown && (item.isAdded || item.isDeleted)) {
          acc.floatDown = {};
        }

        if (item.isAdded) {
          acc.floatDown.floatDownRequested = true;
        }

        if (item.isDeleted) {
          acc.floatDown.floatDownDeleted = true;
        }

        break;
      }

      default:
        break;
    }

    return acc;
  }, {});

  if (!_.isEmpty(result)) {
    return result;
  }
};

// LEN-126: the chattel purpose is saved to the chattel-details row just before the loan-info save (see
// editLoanAndLockSummaryAction) -- only when the form's purpose is chattel, and only when it changed.
export const getChattelPurposeRequestData = ({ form, applicationData }) => {
  const purposeOfChattel = form.purposeOfChattel?.value ?? null;
  const isChattelLoan = checkIsChattelLoanPurpose(form.loanPurpose?.value);
  const isChanged = purposeOfChattel !== (applicationData.purposeOfChattel ?? null);

  return isChattelLoan && isChanged ? purposeOfChattel : null;
};

export const getLoanAndLockRequestBody = ({
  form,
  isLoanNoPpeEngineType,
  applicationData,
  entitySideData,
  withExtensionLimitOverride,
  isExternalRequestType,
  isLockExtensionsV2,
  isProfitAndLossManageAllowed,
  customFields,
  shouldShowOBCustomFieldsSection,
  editedLoanInfoFieldPaths,
  initialForm,
  userEditedFieldNames,
}) => {
  const loanInfo = getLoanInfoPatchRequestData({
    form,
    initialForm,
    userEditedFieldNames,
    isLoanNoPpeEngineType,
    applicationData,
    editedLoanInfoFieldPaths,
  });
  const additionalMortgages = getAdditionalMortgagesTabRequestData({
    form,
    applicationData,
    entitySideData,
  });
  const credits = getCreditsTabRequestData({
    entitySideData,
  });
  const lockInfo = getLockInfoTabRequestData({
    entitySideData,
    withExtensionLimitOverride,
    isExternalRequestType,
    isLockExtensionsV2,
  });
  const loanTagAssignments = getLoanTagAssignmentsTabRequestData({ entitySideData });
  const loanRoleAssignments = getLoanRoleAssignmentsTabRequestData({
    entitySideData,
    applicationData,
  });
  const profitAndLoss = getProfitAndLossTabRequestData({ form });
  const sellSideLock = getSellSideLockTabRequestData({ form, applicationData });
  const programsAndPricing = getProgramsAndPricingTabRequestData({ entitySideData });
  const optimalBlueCustomFields = getOBCustomFieldsTabRequestData({ form, customFields });
  const purposeOfChattel = getChattelPurposeRequestData({ form, applicationData });

  return {
    loanInfo,
    lockInfo,
    additionalMortgages,
    credits,
    loanTagAssignments,
    loanRoleAssignments,
    profitAndLoss: isProfitAndLossManageAllowed ? profitAndLoss : undefined,
    sellSideLock: isProfitAndLossManageAllowed ? sellSideLock : undefined,
    optimalBlueCustomFields: shouldShowOBCustomFieldsSection ? optimalBlueCustomFields : undefined,
    ...programsAndPricing,
    purposeOfChattel,
    requestMetadata: {
      tabId: LoanApplicationTabId.LOAN,
    },
  };
};

export const getNotFoundTableData = fieldName => [
  {
    [fieldName]: {
      position: 1,
      customStyles: emptyMessageCellStyles,
      colSpan: 6,
      content: <NoAvailableMessage className="empty-message-row" message="No results" />,
    },
  },
];

export const prepareModifiedFields = ({
  currentData,
  pendingChangesData,
  initialValuesMap,
  fieldsToExclude,
}) => {
  return currentData.map(values => {
    const pendingChanges = pendingChangesData[values.id];

    const currentPendingChanges = getCurrentEntityPendingChanges({
      initialValues: initialValuesMap[values.id],
      currentValues: values,
    });

    if (!pendingChanges && _.isEmpty(currentPendingChanges)) {
      return {
        ...values,
        pendingChangesActionType: PendingChangesActionType.NO_CHANGES,
      };
    }

    const mergedPendingChanges = _.omit(
      mergePendingChanges(pendingChanges, currentPendingChanges),
      ['isSeparateChange'],
    );

    const modifiedFields = Object.keys(mergedPendingChanges);

    const normalizedModifiedFields = modifiedFields.reduce((acc, field) => {
      const pendingChange = mergedPendingChanges[field];

      if (pendingChange.newValue === pendingChange.initialValue) {
        return acc;
      }

      if (fieldsToExclude.includes(field)) {
        return acc;
      }

      acc.push(_.upperFirst(field));

      return acc;
    }, []);

    const historyActionType = modifiedFields[0]
      ? mergedPendingChanges[modifiedFields[0]]?.actionType
      : '';

    const pendingChangesActionType = HistoryTypeToPendingChangesTypeMap[historyActionType];

    return {
      ...values,
      modifiedFields: normalizedModifiedFields,
      pendingChangesActionType,
    };
  });
};

// Loans API returns a contact's state as { abbreviation, name }. The form's state select opens on the
// abbreviation and holds the picked { name, value } option once changed; SkynetAPI takes the
// abbreviation.
const getContactStateCode = state => (_.isObject(state) ? (state.value ?? null) : (state ?? null));

export const normalizeContactsApplicationData = ({ data }) => {
  return data.reduce((acc, contactData) => {
    const { phoneNumber, directLine, address, ...restContactData } = contactData;

    const formattedData = {
      ...restContactData,
      phoneNumber: normalizeViewPhoneNumber(phoneNumber),
      directLine: normalizeViewPhoneNumber(directLine),
      address: address ? { ...address, state: address.state?.abbreviation ?? null } : null,
    };

    acc[contactData.id] = formattedData;

    return acc;
  }, {});
};

// SkynetAPI keeps a stored NMLS id / state license when the request leaves it null (callers that do not
// load them), so a value the user cleared is sent as an empty string, which clears it.
const CONTACT_CLEARABLE_FIELDS = ['nmlsId', 'stateLicenseId'];

const getClearedContactFields = contactForm =>
  CONTACT_CLEARABLE_FIELDS.reduce(
    (acc, field) =>
      _.isString(contactForm[field]) && !contactForm[field].trim() ? { ...acc, [field]: '' } : acc,
    {},
  );

export const getContactsBodyRequestAction = ({ form, deletedRecords }) => {
  const { update, create } = Object.entries(form).reduce(
    (acc, [contactId, contactForm]) => {
      if (deletedRecords.includes(contactId)) {
        return acc;
      }

      const { contactTypeId, phoneNumber, directLine, address, ...restContactFormFields } =
        contactForm;

      const formattedContactForm = {
        ...trimBodyStringValues(
          replaceEmptyWithNull({
            ...restContactFormFields,
            contactTypeId: contactForm.contactTypeId?.value,
            phoneNumber: normalizeRequestPhoneNumber(phoneNumber),
            directLine: normalizeRequestPhoneNumber(directLine),
            ...address,
            state: getContactStateCode(address?.state),
          }),
        ),
        ...getClearedContactFields(contactForm),
      };

      if (contactId.startsWith(NEW_RECORD_ID)) {
        acc.create.push(formattedContactForm);
      }

      if (!contactId.startsWith(NEW_RECORD_ID)) {
        acc.update[contactId] = formattedContactForm;
      }

      return acc;
    },
    { update: {}, create: [], delete: [...deletedRecords] },
  );

  return {
    update,
    create,
    delete: deletedRecords,
    requestMetadata: {
      tabId: LoanApplicationTabId.LOAN,
    },
  };
};

export const normalizeContactsPendingChanges = ({
  initialPendingChanges,
  formPendingChanges = {},
  newRecords,
  newAddedInitialRecords,
  deletedRecords,
}) => {
  const mergedPendingChanges = mergePendingChanges(initialPendingChanges, formPendingChanges);

  return fillPendingChangesWithActionType({
    pendingChanges: mergedPendingChanges,
    newRecords,
    newAddedInitialRecords,
    deletedRecords,
  });
};

// The chattel-details response is already a flat, camelCase DTO -- no reshaping is needed to
// feed it into the config-driven form (unlike property-and-loan-summary's nested view model).
// LEN-207: a legacy row with only the obsolete isInPark bit opens pre-set to Community / Park, matching
// the summary card; its first save then writes the placement.
export const normalizeChattelApplicationData = ({ data }) => {
  if (!data) {
    return {};
  }

  const sitePlacement = getEffectiveChattelSitePlacement(data);

  return sitePlacement === (data.sitePlacement ?? null) ? data : { ...data, sitePlacement };
};

// The chattel-details endpoint has no cross-user pending-changes feed (see
// selectChattelDetailsPendingChanges), so the nav-tab badge is driven entirely by the live form diff.
export const normalizeChattelPendingChangesAction = ({ formPendingChanges = {} }) =>
  formPendingChanges;

// LEN-156: annualPropertyTax / annualInsurance are retired -- chattel taxes and insurance are entered on
// the standard subject property (Property Info > Property Expenses). They are no longer form fields;
// any stored value only rides through untouched in the applicationData spread below.
const chattelExpensesScalarFields = ['salesPrice', 'salesTax', 'mhit'];

// PUT /chattel-details is a full-document replace on the backend (ChattelDetailsMapper.Apply
// assigns every field unconditionally) -- spread the originally fetched applicationData first so
// fields belonging to sections the user never visited in this modal session aren't nulled out,
// then overlay only the fields that were actually mounted (present as a key in `form`).
// A cleared optional field arrives as '' from the form; persisting that instead of null makes a
// deliberately-emptied field indistinguishable from a set one for any consumer testing IS NULL.
const nullifyBlankStrings = fields => _.mapValues(fields, value => (value === '' ? null : value));

export const getChattelDetailsRequestBody = ({ form, applicationData }) => {
  const sitePlacement =
    'sitePlacement' in form ? (form.sitePlacement?.value ?? null) : applicationData.sitePlacement;
  const isLeasedPrivateProperty = checkIsChattelLeasedPrivateProperty(sitePlacement);
  const isCommunityPark = checkIsChattelCommunityPark(sitePlacement);
  // LEN-133: family land and land contracts have a landowner too - keep name/phone for all three placements.
  const isLandownerPlacement = checkIsChattelLandownerPlacement(sitePlacement);
  const isLandContract = checkIsChattelLandContract(sitePlacement);

  return {
    chattelDetails: {
      ...applicationData,
      ...('condition' in form ? { condition: form.condition?.value ?? null } : {}),
      ...('homeType' in form ? { homeType: form.homeType?.value ?? null } : {}),
      ...('lotModel' in form ? { lotModel: form.lotModel?.value ?? null } : {}),
      ...('valueSource' in form ? { valueSource: form.valueSource?.value ?? null } : {}),
      sitePlacement,
      landownerName: isLandownerPlacement
        ? (form.landownerName ?? applicationData.landownerName)
        : null,
      landownerPhone: isLandownerPlacement
        ? (form.landownerPhone ?? applicationData.landownerPhone) || null
        : null,
      parkName: isCommunityPark ? (form.parkName ?? applicationData.parkName) : null,
      lotNumber: isCommunityPark ? (form.lotNumber ?? applicationData.lotNumber) : null,
      ...nullifyBlankStrings(
        _.pick(form, [
          ...chattelHomeDetailsScalarFieldKeys,
          ...chattelExpensesScalarFields,
          'locationDescription',
        ]),
      ),
      monthlyLotRent:
        isLeasedPrivateProperty || isCommunityPark
          ? (form.monthlyLotRent ?? applicationData.monthlyLotRent)
          : null,
      landMonthlyPayment: isLandContract
        ? (form.landMonthlyPayment ?? applicationData.landMonthlyPayment ?? null)
        : null,
    },
  };
};
